/* eslint-disable no-async-promise-executor */

import { useEffect, useState, useMemo, useCallback } from 'react';

import { 
    IconButton, 
    Chip, 
    Avatar, 
    Button, 
    Tabs, 
    Tab, 
    Box,
    CircularProgress,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { openModal } from "../../../../state/ModalSlice/ModalSlice.ts";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

import BadgeIcon from '@mui/icons-material/Badge';
import { collection, doc, onSnapshot, query, where, Timestamp, DocumentReference, getDocs, setDoc, getDoc } from 'firebase/firestore';

import { db, SECRETS_ENV } from "../../../../Config/firebase.ts"
import { useBackend } from "../../../../hooks/backend/useBackend.ts"
import { setFieldsForAddTeamMemberModal, setFieldsForRevokeRoleModal } from '../../../../state/ArbitrarySlice/ArbitratySlice.ts';
import { selectEffectiveUID, persistActiveRole, selectActiveRole, selectAvailableRoles } from '../../../../state/UserStatesV2/userSliceV2.ts';
import { ServiceProviderRole } from '../../types/UserStateV2';
import { toast } from 'react-toastify';
import { RootState } from '../../../../state/state';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

interface TeamMember {
    name: string;
    number: string;
    role: string;
    status: string;
    businessName: string;
    id: string;
}

interface DelegatedRole {
    name: string;
    number: string;
    role: string;
    serviceProviderReference: DocumentReference;
    businessName: string;
    assignedSubscriptions: DocumentReference[];
    userPhoto: string;
}

interface StaffMember {
    id: string;
    name: string;
    number: string;
    role: string;
    status: string;
    email?: string;
    photo?: string;
    businessName: string;
    serviceProviderId: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`team-tabpanel-${index}`}
            aria-labelledby={`team-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function TeamManagement() {
    const dispatch = useAppDispatch();
    const serviceProviderId = useAppSelector(selectEffectiveUID);
    const currentActiveRole = useAppSelector(selectActiveRole);
    const myRoles = useAppSelector(selectAvailableRoles);
    const userData = useAppSelector((state: RootState) => state.userSliceV2.data);

    const [acceptedRoles, setAcceptedRoles] = useState<DelegatedRole[]>([]);
    const [pendingInvites, setPendingInvites] = useState<TeamMember[]>([]);
    const [subscriptionsMap, setSubscriptionsMap] = useState<Record<string, string>>({});
    const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
    const [syncLoading, setSyncLoading] = useState<boolean>(false);
    const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
    const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
    const [payrollModalOpen, setPayrollModalOpen] = useState<boolean>(false);
    const [attendanceModalOpen, setAttendanceModalOpen] = useState<boolean>(false);
    const [selectedStaffForPayroll, setSelectedStaffForPayroll] = useState<StaffMember | null>(null);
    const [selectedStaffForAttendance, setSelectedStaffForAttendance] = useState<StaffMember | null>(null);
    
    // Attendance state
    const [attendanceYear, setAttendanceYear] = useState<number>(new Date().getFullYear());
    const [attendanceMonth, setAttendanceMonth] = useState<number>(new Date().getMonth());
    const [allStaffAttendance, setAllStaffAttendance] = useState<{[staffId: string]: any[]}>({});
    const [attendanceLoading, setAttendanceLoading] = useState<boolean>(false);
    const [attendanceError, setAttendanceError] = useState<string | null>(null);
    const [editingEntry, setEditingEntry] = useState<any>(null);
    const [selectedStaffForViewing, setSelectedStaffForViewing] = useState<StaffMember | null>(null);
    const [staffSearchQuery, setStaffSearchQuery] = useState<string>('');
    const [isAddingNewEntry, setIsAddingNewEntry] = useState<boolean>(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [entryToDelete, setEntryToDelete] = useState<any>(null);
    const [lockedMonths, setLockedMonths] = useState<{[key: string]: boolean}>({});
    const [loadingLockStatus, setLoadingLockStatus] = useState<boolean>(false);
    
    // Super Admin states
    const [superAdminModalOpen, setSuperAdminModalOpen] = useState<boolean>(false);
    const [superAdminPhoneNumber, setSuperAdminPhoneNumber] = useState<string>('');
    const [superAdminSearchLoading, setSuperAdminSearchLoading] = useState<boolean>(false);
    const [superAdminRequestLoading, setSuperAdminRequestLoading] = useState<boolean>(false);
    const [superAdminSearchResult, setSuperAdminSearchResult] = useState<{
        found: boolean;
        academyName?: string;
        phoneNumber?: string;
        serviceProviderId?: string;
    } | null>(null);
    const [sentRequests, setSentRequests] = useState<any[]>([]);
    const [receivedRequests, setReceivedRequests] = useState<any[]>([]);
    const [superAdminRequestsLoading, setSuperAdminRequestsLoading] = useState<boolean>(false);
    const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
    const [mySuperAdminRoles, setMySuperAdminRoles] = useState<any[]>([]); // Accounts I have access to
    const [mySuperAdmins, setMySuperAdmins] = useState<any[]>([]); // Admins who have access to my account
    
    // Get axios function from useBackend
    const { axiosPostRequest } = useBackend('');
    
    // Team Overview section states
    const [myRolesExpanded, setMyRolesExpanded] = useState(true);
    const [delegatedRolesExpanded, setDelegatedRolesExpanded] = useState(true);
    const [pendingInvitesExpanded, setPendingInvitesExpanded] = useState(true);
    const [delegatedRolesSearch, setDelegatedRolesSearch] = useState('');
    
    // Filter delegated roles based on search
    const filteredDelegatedRoles = useMemo(() => {
        if (!delegatedRolesSearch.trim()) return acceptedRoles;
        const searchLower = delegatedRolesSearch.toLowerCase();
        return acceptedRoles.filter(role => 
            role.name.toLowerCase().includes(searchLower) ||
            role.number.toLowerCase().includes(searchLower) ||
            role.role.toLowerCase().includes(searchLower) ||
            role.businessName.toLowerCase().includes(searchLower)
        );
    }, [acceptedRoles, delegatedRolesSearch]);

    // Filter staff members based on search query
    const filteredStaffMembers = useMemo(() => {
        if (!staffSearchQuery.trim()) return staffMembers;
        const searchLower = staffSearchQuery.toLowerCase();
        return staffMembers.filter(staff => 
            staff.name.toLowerCase().includes(searchLower) ||
            staff.number.toLowerCase().includes(searchLower) ||
            staff.role.toLowerCase().includes(searchLower) ||
            (staff.email && staff.email.toLowerCase().includes(searchLower))
        );
    }, [staffMembers, staffSearchQuery]);

    // Get saved tab from localStorage or URL query parameter or default to 0 (Team Members)
    const [tabValue, setTabValue] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam) {
            return parseInt(tabParam);
        }
        const saved = localStorage.getItem('teamManagementTab');
        return saved ? parseInt(saved) : 0;
    });

    

    // Add isOwner check
    const isOwner = !currentActiveRole;
    
    // Check if user is owner or administrator
    const currentUserRole = acceptedRoles.find(role => role.number === userData?.phone_number);

    const isOwnerOrAdmin = isOwner || currentUserRole?.role === 'Administrator' || currentActiveRole?.role === 'Administrator';


    const fetchSubscriptions = useCallback(() => {
        if (!serviceProviderId) return () => { };

        const subscriptionsQuery = query(
            collection(db, "ServiceProviders", serviceProviderId, "Subscriptions")
        );

        const unsubscribe = onSnapshot(subscriptionsQuery, (snapshot) => {
            const subscriptions: Record<string, string> = {};
            snapshot.forEach((doc) => {
                const data = doc.data();
                const subscriptionName = data.Name || data.name || data.title || data.subscriptionName || data.planName || data.displayName || `Subscription ${doc.id}`;
                subscriptions[doc.id] = subscriptionName;
            });
            setSubscriptionsMap(subscriptions);
        }, (error) => {
            console.error('Error fetching subscriptions:', error);
            toast.error('Failed to fetch subscription details');
        });

        return unsubscribe;
    }, [serviceProviderId]);

    const fetchAcceptedRoles = useCallback(() => {
        if (!serviceProviderId) return () => { };

        const acceptedRolesRef = doc(db, "ServiceProviders", serviceProviderId);

        const unsubscribe = onSnapshot(acceptedRolesRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const delegatedRoles = data.delegatedRoles || [];
                setAcceptedRoles(delegatedRoles);
            } else {
                setAcceptedRoles([]);
            }
        }, (error) => {
            console.error('Error fetching accepted roles:', error);
            toast.error('Failed to fetch team member details');
        });

        return unsubscribe;
    }, [serviceProviderId]);

    const fetchTeamMembers = useCallback(() => {
        if (!serviceProviderId) return () => { };

        const pendingQuery = query(
            collection(db, "PendingActions"),
            where("owner", "==", doc(db, "ServiceProviders", serviceProviderId)),
            where("actionType", "==", "grantRole"),
            where("status", "==", "pending")
        );

        const unsubscribe = onSnapshot(pendingQuery, (pendingSnapshot) => {
            const pending: TeamMember[] = [];
            pendingSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.roleAssigned && typeof data.roleAssigned === 'object') {
                    pending.push({
                        name: typeof data.roleAssigned.name === 'string' ? data.roleAssigned.name : '',
                        number: typeof data.roleAssigned.number === 'string' ? data.roleAssigned.number : '',
                        role: typeof data.roleAssigned.role === 'string' ? data.roleAssigned.role : '',
                        status: typeof data.status === 'string' ? data.status : '',
                        businessName: typeof data.businessName === 'string' ? data.businessName : '',
                        id: doc.id
                    });
                }
            });
            setPendingInvites(pending);
        }, (error) => {
            console.error('Error fetching pending invites:', error);
            toast.error('Failed to fetch pending invites');
        });

        return unsubscribe;
    }, [serviceProviderId]);

    // Remove fetchRoleHistory and all staff subcollection logic
    // Remove didAutoSync, migrationLoading, migrationProgress, handleMigrateToStaff, and related useEffect
    // Remove all code related to roleHistory, staff cards, and sync button
    // Only keep PendingActions and delegatedRoles logic for role management

    const loadStaffMembers = useCallback(async () => {
        try {
            const staffCollectionRef = collection(db, 'ServiceProviders', serviceProviderId, 'Staff');
            const staffSnapshot = await getDocs(staffCollectionRef);
            
            const staff: StaffMember[] = [];
            staffSnapshot.forEach((doc) => {
                const data = doc.data();
                staff.push({
                    id: doc.id,
                    name: data.name,
                    number: data.number,
                    role: data.role,
                    status: data.status,
                    email: data.email,
                    photo: data.photo,
                    businessName: data.businessName,
                    serviceProviderId: data.serviceProviderId
                });
            });
            
            // Add owner as default staff member if userData is available and owner is not already in the list
            if (userData) {
                // Check if owner already exists in the staff list (from Staff subcollection)
                const ownerExists = staff.some(staffMember => 
                    staffMember.serviceProviderId === serviceProviderId || 
                    staffMember.id === serviceProviderId ||
                    staffMember.id === 'owner'
                );
                
                if (!ownerExists) {
                    const ownerStaffMember: StaffMember = {
                        id: userData.uid, // Use actual ServiceProvider ID instead of 'owner'
                        name: userData.display_name || userData.email || 'Owner',
                        number: userData.phone_number || '',
                        role: 'Owner',
                        status: 'active',
                        email: userData.email || '',
                        photo: userData.photo_url || '',
                        businessName: userData.businessProfile?.businessName || '',
                        serviceProviderId: userData.uid
                    };
                    
                    // Add owner at the beginning of the list
                    staff.unshift(ownerStaffMember);
                }
            }
            
            setStaffMembers(staff);
        } catch (error) {
            console.error("Error loading staff members:", error);
            toast.error("Failed to load staff members");
        }
    }, [serviceProviderId, userData]);

    // Load all staff attendance data for the selected month
    const loadAllStaffAttendance = useCallback(async () => {
        if (!staffMembers.length) return;
        
        setAttendanceLoading(true);
        setAttendanceError(null);
        
        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            const attendanceData: {[staffId: string]: any[]} = {};
            
            // Load attendance for each staff member
            const promises = staffMembers.map(async (staff) => {
                try {
                    const attendanceRef = doc(
                        db, 
                        'ServiceProviders', 
                        serviceProviderId, 
                        'Staff', 
                        staff.id, 
                        'Attendance', 
                        monthKey
                    );
                    
                    const attendanceSnap = await getDoc(attendanceRef);
                    if (attendanceSnap.exists()) {
                        const data = attendanceSnap.data();
                        attendanceData[staff.id] = data.dailyEntries || [];
                    } else {
                        attendanceData[staff.id] = [];
                    }
                } catch (error) {
                    console.error(`Error loading attendance for ${staff.name}:`, error);
                    attendanceData[staff.id] = [];
                }
            });
            
            await Promise.all(promises);
            setAllStaffAttendance(attendanceData);
        } catch (error) {
            console.error('Error loading all staff attendance:', error);
            setAttendanceError('Failed to load attendance data');
            toast.error('Failed to load attendance data');
        } finally {
            setAttendanceLoading(false);
        }
    }, [staffMembers, attendanceYear, attendanceMonth, serviceProviderId]);

    // Set up all listeners
    useEffect(() => {
        // setHistoryLoading(true); // This line is removed as per the edit hint
        const unsubscribePending = fetchTeamMembers();
        const unsubscribeAccepted = fetchAcceptedRoles();
        const unsubscribeSubscriptions = fetchSubscriptions();
        // const unsubscribeHistory = fetchRoleHistory(); // This line is removed as per the edit hint
        
        return () => {
            unsubscribePending();
            unsubscribeAccepted();
            unsubscribeSubscriptions();
            // unsubscribeHistory(); // This line is removed as per the edit hint
        };
    }, [serviceProviderId, fetchTeamMembers, fetchAcceptedRoles, fetchSubscriptions]);

    // Load staff members when Staff or Attendance tab is selected
    useEffect(() => {
        if (serviceProviderId && (tabValue === 1 || tabValue === 2)) { // Staff tab (1) or Attendance tab (2)
            loadStaffMembers();
        }
    }, [tabValue, serviceProviderId, loadStaffMembers]);

    // Load all staff attendance when Attendance tab is accessed or when month/year changes
    useEffect(() => {
        if (tabValue === 2 && staffMembers.length > 0) { // Attendance tab index (updated after removing payroll)
            loadAllStaffAttendance();
        }
    }, [tabValue, attendanceYear, attendanceMonth, staffMembers.length, serviceProviderId, loadAllStaffAttendance]);

    // Load locked months from Firestore
    const loadLockedMonths = useCallback(async () => {
        if (!serviceProviderId) return;
        
        try {
            setLoadingLockStatus(true);
            const lockRef = doc(db, 'ServiceProviders', serviceProviderId, 'Settings', 'attendanceLocks');
            const lockDoc = await getDoc(lockRef);
            
            if (lockDoc.exists()) {
                setLockedMonths(lockDoc.data().lockedMonths || {});
            }
        } catch (error) {
            console.error('Error loading locked months:', error);
        } finally {
            setLoadingLockStatus(false);
        }
    }, [serviceProviderId]);

    // Load locked months when component mounts or service provider changes
    useEffect(() => {
        if (serviceProviderId) {
            loadLockedMonths();
        }
    }, [serviceProviderId, loadLockedMonths]);

    // Fetch super admin requests
    const fetchSuperAdminRequests = useCallback(async () => {
        if (!serviceProviderId) return;
        
        setSuperAdminRequestsLoading(true);
        try {
            // Fetch requests sent by me (initiator)
            const sentQuery = query(
                collection(db, 'SuperAdminRequests'),
                where('initiatorSPID', '==', serviceProviderId)
            );
            const sentSnapshot = await getDocs(sentQuery);
            const sentData = sentSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSentRequests(sentData);

            // Fetch requests received by me (approver)
            const receivedQuery = query(
                collection(db, 'SuperAdminRequests'),
                where('approverSPID', '==', serviceProviderId)
            );
            const receivedSnapshot = await getDocs(receivedQuery);
            const receivedData = receivedSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setReceivedRequests(receivedData);

            // Fetch my super admin roles (accounts I have access to) from /SuperAdminAccess
            const myRolesQuery = query(
                collection(db, 'SuperAdminRequests'),
                where('initiatorSPID', '==', serviceProviderId),
                where('status', '==', 'approved')
            );
            const myRolesSnapshot = await getDocs(myRolesQuery);
            const myRolesData = myRolesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMySuperAdminRoles(myRolesData);

            // Fetch my super admins (admins who have access to my account) from /ServiceProviders/{spid}/superAdmins
            const mySuperAdminsSnapshot = await getDocs(
                collection(db, `ServiceProviders/${serviceProviderId}/SuperAdmins`)
            );
            const mySuperAdminsData = mySuperAdminsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMySuperAdmins(mySuperAdminsData);
        } catch (error) {
            console.error('Error fetching super admin requests:', error);
        } finally {
            setSuperAdminRequestsLoading(false);
        }
    }, [serviceProviderId]);

    // Load super admin requests when tab changes to Super Admin tab
    useEffect(() => {
        if (tabValue === 3 && serviceProviderId) {
            fetchSuperAdminRequests();
        }
    }, [tabValue, serviceProviderId, fetchSuperAdminRequests]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        localStorage.setItem('teamManagementTab', newValue.toString());
    };

    const handleAddMember = () => {
        if (!isOwnerOrAdmin) {
            toast.error("Only the owner or administrator can add team members");
            return;
        }
        dispatch(setFieldsForAddTeamMemberModal({
            actionType: "grantRole",
            name: "",
            number: "",
            role: "Administrator",
            selectedSubscriptions: [],
            userPhoto: ""
        }));
        dispatch(openModal("AddTeamMemberModalOpen"));
    };

    const handleAddDelegatedRole = () => {
        if (!isOwnerOrAdmin) {
            toast.error("Only the owner or administrator can add delegated roles");
            return;
        }
        dispatch(setFieldsForAddTeamMemberModal({
            actionType: "grantRole",
            name: "",
            number: "",
            role: "Administrator",
            selectedSubscriptions: [],
            userPhoto: ""
        }));
        dispatch(openModal("AddTeamMemberModalOpen"));
    };

    const handleEditDelegatedRole = (role: DelegatedRole) => {
        if (!isOwnerOrAdmin) {
            toast.error("Only the owner or administrator can edit delegated roles");
            return;
        }
        
        // Prevent administrators from editing their own role
        if (!isOwner && role.number === userData?.phone_number) {
            toast.error("You cannot edit your own role");
            return;
        }
        
        dispatch(setFieldsForAddTeamMemberModal({
            actionType: "editRole",
            name: role.name,
            number: role.number,
            role: role.role,
            selectedSubscriptions: role.assignedSubscriptions || [],
            userPhoto: role.userPhoto || "",
            oldRole: role
        }));
        dispatch(openModal("AddTeamMemberModalOpen"));
    };

    const handleDeleteDelegatedRole = (role: DelegatedRole) => {
        if (!isOwnerOrAdmin) {
            toast.error("Only the owner or administrator can delete delegated roles");
            return;
        }
        
        // Prevent administrators from deleting their own role
        if (!isOwner && role.number === userData?.phone_number) {
            toast.error("You cannot delete your own role");
            return;
        }
        
        dispatch(setFieldsForRevokeRoleModal({
            id: role.serviceProviderReference?.id,
            name: role.name,
            number: role.number,
            businessName: role.businessName,
            role: role.role,
            isAccepted: true,
            actionType: 'revokeRole'
        }));
        dispatch(openModal("RevokeRoleModalOpen"));
    };

    const handleSoftDeleteAttendance = async (staffId: string, entryId: string) => {
        // Check if month is locked
        if (isCurrentMonthLocked()) {
            toast.error('Cannot delete attendance entry - month is locked');
            return;
        }

        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                serviceProviderId, 
                'Staff', 
                staffId, 
                'Attendance', 
                monthKey
            );
            
            const existingAttendance = allStaffAttendance[staffId] || [];
            const updatedEntries = existingAttendance.map(entry => 
                entry.id === entryId 
                    ? {
                        ...entry,
                        status: 'deleted',
                        deletedAt: new Date(),
                        deletedBy: userData?.display_name || userData?.email || 'Unknown',
                        auditLog: [
                            ...(entry.auditLog || []),
                            {
                                action: 'deleted',
                                timestamp: new Date(),
                                user: userData?.display_name || userData?.email || 'Unknown',
                                details: `Marked attendance entry as deleted (soft delete)`
                            }
                        ]
                    }
                    : entry
            );
            
            await setDoc(attendanceRef, {
                staffId: staffId,
                staffName: staffMembers.find(s => s.id === staffId)?.name || 'Unknown',
                year: attendanceYear,
                month: attendanceMonth + 1,
                dailyEntries: updatedEntries,
                lastUpdated: new Date()
            }, { merge: true });
            
            // Update local state
            setAllStaffAttendance(prev => ({
                ...prev,
                [staffId]: updatedEntries
            }));
            
            setShowDeleteConfirm(false);
            setEntryToDelete(null);
            setEditingEntry(null);
            toast.success('Attendance entry marked as deleted');
        } catch (error) {
            console.error('Error soft deleting attendance:', error);
            toast.error('Failed to delete attendance entry');
        }
    };

    const handleAddAbsentEntry = async (staffId: string, date: string) => {
        // Check if month is locked
        if (isCurrentMonthLocked()) {
            toast.error('Cannot mark as absent - month is locked');
            return;
        }

        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                serviceProviderId, 
                'Staff', 
                staffId, 
                'Attendance', 
                monthKey
            );
            
            const newEntry = {
                id: Date.now().toString(),
                date: date,
                subscriptions: [],
                totalHours: 0,
                status: 'absent',
                createdAt: new Date(),
                createdBy: userData?.display_name || userData?.email || 'Unknown',
                auditLog: [{
                    action: 'created',
                    timestamp: new Date(),
                    user: userData?.display_name || userData?.email || 'Unknown',
                    details: `Explicitly marked as absent`
                }]
            };
            
            // Get existing entries for this month
            const existingAttendance = allStaffAttendance[staffId] || [];
            
            // Remove any existing entries for this date (mark as deleted or filter out)
            const filteredEntries = existingAttendance.filter((entry: any) => {
                if (entry.date === date && entry.status !== 'deleted') {
                    // Mark existing entry as deleted instead of removing it (for audit trail)
                    return false;
                }
                return true;
            });
            
            // Add audit log entries for deleted entries
            const deletedEntries = existingAttendance.filter((entry: any) => 
                entry.date === date && entry.status !== 'deleted'
            ).map((entry: any) => ({
                ...entry,
                status: 'deleted',
                deletedAt: new Date(),
                deletedBy: userData?.display_name || userData?.email || 'Unknown',
                auditLog: [
                    ...(entry.auditLog || []),
                    {
                        action: 'deleted',
                        timestamp: new Date(),
                        user: userData?.display_name || userData?.email || 'Unknown',
                        details: `Deleted because marking date as absent`
                    }
                ]
            }));
            
            // Combine filtered entries, deleted entries, and new absent entry
            const updatedEntries = [...filteredEntries, ...deletedEntries, newEntry];
            
            await setDoc(attendanceRef, {
                staffId: staffId,
                staffName: staffMembers.find(s => s.id === staffId)?.name || 'Unknown',
                year: attendanceYear,
                month: attendanceMonth + 1,
                dailyEntries: updatedEntries,
                lastUpdated: new Date()
            }, { merge: true });
            
            // Update local state
            setAllStaffAttendance(prev => ({
                ...prev,
                [staffId]: updatedEntries
            }));
            
            toast.success('Absent entry added successfully');
        } catch (error) {
            console.error('Error adding absent entry:', error);
            toast.error('Failed to add absent entry');
        }
    };

    // Check if current month is locked
    const isCurrentMonthLocked = () => {
        const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
        return lockedMonths[monthKey] || false;
    };

    // Toggle month lock status (admin only)
    const toggleMonthLock = async () => {
        if (!isOwner && !acceptedRoles.find(role => role.number === userData?.phone_number && role.role === 'Administrator')) {
            toast.error('Only admins can lock/unlock months');
            return;
        }

        const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
        const isCurrentlyLocked = lockedMonths[monthKey] || false;
        
        try {
            const lockRef = doc(db, 'ServiceProviders', serviceProviderId, 'Settings', 'attendanceLocks');
            const updatedLocks = {
                ...lockedMonths,
                [monthKey]: !isCurrentlyLocked
            };
            
            await setDoc(lockRef, {
                lockedMonths: updatedLocks,
                lastUpdated: new Date(),
                updatedBy: userData?.display_name || userData?.email || 'Unknown'
            }, { merge: true });
            
            setLockedMonths(updatedLocks);
            toast.success(isCurrentlyLocked ? 'Month unlocked successfully' : 'Month locked successfully');
        } catch (error) {
            console.error('Error toggling month lock:', error);
            toast.error('Failed to update month lock status');
        }
    };





    const handleRoleSwitch = async (role: ServiceProviderRole) => {
        try {
            await dispatch(persistActiveRole(role));
            toast.success(`Switched to ${role.businessName} as ${role.role}`);
        } catch (error) {
            console.error("Error switching role:", error);
            toast.error("Failed to switch role. Please try again.");
        }
    };

    const handleClearRole = async () => {
        try {
            await dispatch(persistActiveRole(null));
            toast.success("Successfully switched to personal account");
        } catch (error) {
            console.error("Error clearing active role:", error);
            toast.error("Failed to switch to personal account. Please try again.");
        }
    };

    const syncStaffFromDelegatedRoles = async () => {
        if (!isOwner) {
            toast.error("Only the owner can sync staff data");
            return;
        }

        setSyncLoading(true);
        try {
            // Get the owner's service provider document
            const ownerDocRef = doc(db, 'ServiceProviders', serviceProviderId);
            const ownerDoc = await getDoc(ownerDocRef);
            
            if (!ownerDoc.exists()) {
                toast.error("Owner service provider document not found");
                return;
            }

            const ownerData = ownerDoc.data();
            const delegatedRoles = ownerData?.delegatedRoles || [];

            if (delegatedRoles.length === 0) {
                toast.info("No delegated roles found to sync");
                return;
            }

            const staffCollectionRef = collection(db, 'ServiceProviders', serviceProviderId, 'Staff');
            const syncedStaff: StaffMember[] = [];
            let syncedCount = 0;
            let skippedCount = 0;

            for (const role of delegatedRoles) {
                try {
                    // Look up ServiceProvider by phone number
                    const serviceProvidersQuery = query(
                        collection(db, 'ServiceProviders'),
                        where('phone_number', '==', role.number)
                    );
                    const serviceProvidersSnapshot = await getDocs(serviceProvidersQuery);
                    
                    if (serviceProvidersSnapshot.empty) {
                        console.log(`ServiceProvider not found for phone: ${role.number}`);
                        skippedCount++;
                        continue;
                    }

                    const serviceProviderDoc = serviceProvidersSnapshot.docs[0];
                    const serviceProviderId = serviceProviderDoc.id;
                    const serviceProviderData = serviceProviderDoc.data();

                    // Create staff document
                    const staffDocRef = doc(staffCollectionRef, serviceProviderId);
                    const staffData = {
                        name: role.name,
                        number: role.number,
                        role: role.role,
                        status: role.assignmentStatus === 'Active' ? 'Active' : 'Inactive',
                        email: serviceProviderData.email || '',
                        photo: role.userPhoto || '',
                        businessName: role.businessName,
                        serviceProviderId: serviceProviderId,
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now()
                    };

                    await setDoc(staffDocRef, staffData);
                    
                    syncedStaff.push({
                        id: serviceProviderId,
                        ...staffData
                    });
                    syncedCount++;

                } catch (error) {
                    console.error(`Error syncing staff member ${role.name}:`, error);
                    skippedCount++;
                }
            }

            setStaffMembers(syncedStaff);
            toast.success(`Successfully synced ${syncedCount} staff members. ${skippedCount} skipped.`);

        } catch (error) {
            console.error("Error syncing staff:", error);
            toast.error("Failed to sync staff data. Please try again.");
        } finally {
            setSyncLoading(false);
        }
    };

    const handleEditStaff = (staff: StaffMember) => {
        setEditingStaffId(staff.id);
        setEditingStaff({ ...staff });
    };

    const handleCancelEdit = () => {
        setEditingStaffId(null);
        setEditingStaff(null);
    };

    const handleSaveStaff = async () => {
        if (!editingStaff) return;

        setSyncLoading(true);
        try {
            const staffDocRef = doc(db, 'ServiceProviders', serviceProviderId, 'Staff', editingStaff.id);
            const staffData = {
                name: editingStaff.name,
                number: editingStaff.number,
                role: editingStaff.role,
                status: editingStaff.status,
                email: editingStaff.email || '',
                photo: editingStaff.photo || '',
                businessName: editingStaff.businessName,
                serviceProviderId: editingStaff.serviceProviderId,
                updatedAt: Timestamp.now()
            };

            await setDoc(staffDocRef, staffData, { merge: true });
            
            // Update local state
            setStaffMembers(prev => prev.map(staff => 
                staff.id === editingStaff.id 
                    ? { ...editingStaff, ...staffData }
                    : staff
            ));

            setEditingStaffId(null);
            setEditingStaff(null);
            toast.success("Staff member updated successfully");

        } catch (error) {
            console.error("Error updating staff member:", error);
            toast.error("Failed to update staff member. Please try again.");
        } finally {
            setSyncLoading(false);
        }
    };

    const updateEditingStaff = (field: keyof StaffMember, value: string) => {
        if (editingStaff) {
            setEditingStaff({ ...editingStaff, [field]: value });
        }
    };

    const handleAccessPayroll = (staff: StaffMember) => {
        setSelectedStaffForPayroll(staff);
        setPayrollModalOpen(true);
    };

    const handleAccessAttendance = (staff: StaffMember) => {
        setSelectedStaffForAttendance(staff);
        setAttendanceModalOpen(true);
    };

    const handleClosePayrollModal = () => {
        setPayrollModalOpen(false);
        setSelectedStaffForPayroll(null);
    };

    const handleCloseAttendanceModal = () => {
        setAttendanceModalOpen(false);
        setSelectedStaffForAttendance(null);
    };



    // Check if current user can manage attendance for a specific staff member
    const canManageAttendance = (staffMember: StaffMember) => {
        // Owner can manage everyone's attendance
        if (isOwner) return true;
        
        // Staff can only manage their own attendance
        if (userData?.phone_number === staffMember.number || userData?.email === staffMember.email) {
            return true;
        }
        
        return false;
    };

    // Get subscriptions available for attendance entry - filters based on target staff member's role
    const getSubscriptionsForAttendanceEntry = (targetStaffId?: string) => {
        // If no targetStaffId provided, use current user (for backwards compatibility)
        let targetStaff;
        
        if (targetStaffId) {
            // Find the target staff member we're marking attendance for
            targetStaff = staffMembers.find(s => s.id === targetStaffId);
            
            // Check if target is owner (either by ID 'owner' or by matching userData.uid)
            const isOwner = targetStaffId === 'owner' || targetStaffId === userData?.uid || targetStaff?.role === 'Owner';
            
            if (isOwner) {
                // Owner can access all subscriptions
                console.log('🔍 Team Management - Target is Owner, returning all subscriptions');
                const allSubs = Object.entries(subscriptionsMap || {}).map(([id, name]) => ({
                    id,
                    Name: name,
                    Description: ''
                }));
                return allSubs;
            } else {
                // Find the target staff member's role in delegated roles
                const targetStaffRole = acceptedRoles.find(role => role.number === targetStaff?.number);
                
                console.log('🔍 Team Management - Role-based subscription filtering for:', targetStaff?.name);
                console.log('targetStaffRole:', targetStaffRole);
                console.log('subscriptionsMap keys:', Object.keys(subscriptionsMap || {}));
                
                // Check if staff member is Administrator (either from delegated roles or Staff document)
                const isAdministrator = targetStaffRole?.role === 'Administrator' || targetStaff?.role === 'Administrator';
                
                if (isAdministrator) {
                    // Admin role can access all subscriptions
                    const allSubs = Object.entries(subscriptionsMap || {}).map(([id, name]) => ({
                        id,
                        Name: name,
                        Description: ''
                    }));
                    console.log('✅ Target is Admin - returning all subscriptions:', allSubs.length);
                    return allSubs;
                } else if (targetStaffRole?.assignedSubscriptions) {
                    // Manager, Observer, Staff can only access their assigned subscriptions
                    const availableSubIds = targetStaffRole.assignedSubscriptions.map((ref: any) => {
                        const pathParts = ref.path.split('/');
                        return pathParts[pathParts.length - 1];
                    });
                    
                    const filteredSubs = Object.entries(subscriptionsMap || {})
                        .filter(([id]) => availableSubIds.includes(id))
                        .map(([id, name]) => ({
                            id,
                            Name: name,
                            Description: ''
                        }));
                    console.log('✅ Target staff role - available subscription IDs:', availableSubIds);
                    console.log('✅ Target staff role - returning filtered subscriptions:', filteredSubs.length);
                    return filteredSubs;
                } else {
                    // No role found or no assigned subscriptions for target staff
                    console.log('❌ No role found or no assigned subscriptions for target staff');
        return [];
                }
            }
        } else {
            // Fallback: Check current user's permissions (for self-service)
            const currentUserRole = acceptedRoles.find(role => role.number === userData?.phone_number);
            
            console.log('🔍 Team Management - Role-based subscription filtering (current user):');
            console.log('isOwner:', isOwner);
            console.log('currentUserRole:', currentUserRole);
            
            if (isOwner || currentUserRole?.role === 'Administrator') {
                const allSubs = Object.entries(subscriptionsMap || {}).map(([id, name]) => ({
                    id,
                    Name: name,
                    Description: ''
                }));
                console.log('✅ Current user is Owner/Admin - returning all subscriptions:', allSubs.length);
                return allSubs;
            } else if (currentUserRole?.assignedSubscriptions) {
                const availableSubIds = currentUserRole.assignedSubscriptions.map((ref: any) => {
                    const pathParts = ref.path.split('/');
                    return pathParts[pathParts.length - 1];
                });
                
                const filteredSubs = Object.entries(subscriptionsMap || {})
                    .filter(([id]) => availableSubIds.includes(id))
                    .map(([id, name]) => ({
                        id,
                        Name: name,
                        Description: ''
                    }));
                console.log('✅ Current user staff role - returning filtered subscriptions:', filteredSubs.length);
                return filteredSubs;
            } else {
                console.log('❌ No role found for current user');
                return [];
            }
        }
    };

    // Add new attendance entry
    const handleAddAttendanceEntry = async (attendanceData: {
        staffId: string;
        date: string;
        subscriptions: {subscriptionId: string; subscriptionName: string; hours: number}[];
        totalHours: number;
        status?: string;
    }) => {
        // Check if month is locked
        if (isCurrentMonthLocked()) {
            toast.error('Cannot add attendance entry - month is locked');
            return;
        }
        
        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            
            // Use the provided staffId, but create Staff document with userData.uid if it doesn't exist
            // This handles cases where owner or users mark attendance but their Staff document doesn't exist
            let finalStaffId = attendanceData.staffId;
            
            // Check if the staff document exists, if not, create it with userData.uid
            try {
                const staffDocRef = doc(db, 'ServiceProviders', serviceProviderId, 'Staff', attendanceData.staffId);
                const staffDoc = await getDoc(staffDocRef);
                if (!staffDoc.exists()) {
                    // Create Staff document with userData.uid as the document ID
                    const userStaffDocRef = doc(db, 'ServiceProviders', serviceProviderId, 'Staff', userData?.uid || 'unknown');
                    const staffData = {
                        name: userData?.display_name || 'Unknown User',
                        number: userData?.phone_number || '',
                        role: currentActiveRole?.role || 'Owner',
                        status: 'Active',
                        email: userData?.email || '',
                        photo: userData?.photo_url || '',
                        businessName: userData?.businessProfile?.businessName || '',
                        serviceProviderId: userData?.uid || 'unknown',
                        createdAt: Timestamp.now(),
                        updatedAt: Timestamp.now()
                    };
                    
                    await setDoc(userStaffDocRef, staffData);
                    finalStaffId = userData?.uid || attendanceData.staffId;
                }
            } catch (error) {
                console.warn('Error checking/creating staff document, using fallback:', error);
                finalStaffId = userData?.uid || attendanceData.staffId;
            }
            
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                serviceProviderId, 
                'Staff', 
                finalStaffId, 
                'Attendance', 
                monthKey
            );
            
            const entryStatus = attendanceData.status || (attendanceData.totalHours > 0 ? 'present' : 'absent');
            const newEntry = {
                id: Date.now().toString(),
                date: attendanceData.date,
                subscriptions: attendanceData.subscriptions,
                totalHours: attendanceData.totalHours,
                status: entryStatus, // 'present', 'absent', 'deleted'
                createdAt: new Date(),
                createdBy: userData?.display_name || userData?.email || 'Unknown',
                auditLog: [{
                    action: 'created',
                    timestamp: new Date(),
                    user: userData?.display_name || userData?.email || 'Unknown',
                    details: entryStatus === 'absent' 
                        ? 'Explicitly marked as absent' 
                        : `Created attendance entry with ${attendanceData.totalHours} hours`
                }]
            };
            
            // Get existing entries for this month using the final staff ID
            const existingAttendance = allStaffAttendance[finalStaffId] || [];
            
            let updatedEntries;
            
            if (entryStatus === 'absent') {
                // When marking as absent, remove any existing entries for this date
                const filteredEntries = existingAttendance.filter((entry: any) => {
                    if (entry.date === attendanceData.date && entry.status !== 'deleted') {
                        return false; // Remove existing entries for this date
                    }
                    return true;
                });
                
                // Mark deleted entries for audit trail
                const deletedEntries = existingAttendance.filter((entry: any) => 
                    entry.date === attendanceData.date && entry.status !== 'deleted'
                ).map((entry: any) => ({
                    ...entry,
                    status: 'deleted',
                    deletedAt: new Date(),
                    deletedBy: userData?.display_name || userData?.email || 'Unknown',
                    auditLog: [
                        ...(entry.auditLog || []),
                        {
                            action: 'deleted',
                            timestamp: new Date(),
                            user: userData?.display_name || userData?.email || 'Unknown',
                            details: `Deleted because marking date as absent`
                        }
                    ]
                }));
                
                updatedEntries = [...filteredEntries, ...deletedEntries, newEntry];
            } else {
                // For present entries, just add normally
                updatedEntries = [...existingAttendance, newEntry];
            }
            
            await setDoc(attendanceRef, {
                staffId: attendanceData.staffId,
                staffName: staffMembers.find(s => s.id === attendanceData.staffId)?.name || 'Unknown',
                year: attendanceYear,
                month: attendanceMonth + 1,
                dailyEntries: updatedEntries,
                lastUpdated: new Date()
            }, { merge: true });
            
            // Update local state
            setAllStaffAttendance(prev => ({
                ...prev,
                [attendanceData.staffId]: updatedEntries
            }));
            
            toast.success('Attendance entry added successfully');
        } catch (error) {
            console.error('Error adding attendance:', error);
            toast.error('Failed to add attendance entry');
        }
    };

    // Edit attendance entry
    const handleEditAttendanceEntry = async (
        staffId: string, 
        entryId: string, 
        attendanceData: {
            date: string;
            subscriptions: {subscriptionId: string; subscriptionName: string; hours: number}[];
            totalHours: number;
        }
    ) => {
        // Check if month is locked
        if (isCurrentMonthLocked()) {
            toast.error('Cannot edit attendance entry - month is locked');
            return;
        }
        
        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                serviceProviderId, 
                'Staff', 
                staffId, 
                'Attendance', 
                monthKey
            );
            
            const existingAttendance = allStaffAttendance[staffId] || [];
            
            // If we're converting from absent to present, also remove any other absent entries for the same date
            const targetDate = attendanceData.date;
            const newStatus = attendanceData.totalHours > 0 ? 'present' : 'absent';
            
            let updatedEntries = existingAttendance.map(entry => {
                if (entry.id === entryId) {
                    // This is the entry being edited
                    const previousStatus = entry.status;
                    
                    // Create detailed audit log entry
                    let statusChangeDetails = '';
                    if (previousStatus !== newStatus) {
                        statusChangeDetails = ` (Status changed from ${previousStatus} to ${newStatus})`;
                    }
                    
                    return {
                        ...entry,
                        date: attendanceData.date,
                        subscriptions: attendanceData.subscriptions,
                        totalHours: attendanceData.totalHours,
                        status: newStatus, // Update status based on hours
                        updatedAt: new Date(),
                        updatedBy: userData?.display_name || userData?.email || 'Unknown',
                        auditLog: [
                            ...(entry.auditLog || []),
                            {
                                action: 'updated',
                                timestamp: new Date(),
                                user: userData?.display_name || userData?.email || 'Unknown',
                                details: `Updated attendance entry to ${attendanceData.totalHours} hours${statusChangeDetails}`,
                                previousValues: {
                                    subscriptions: entry.subscriptions,
                                    totalHours: entry.totalHours,
                                    status: previousStatus
                                }
                            }
                        ]
                    };
                }
                return entry;
            });
            
            // If converting to present (adding hours), remove any other absent entries for the same date
            if (newStatus === 'present') {
                updatedEntries = updatedEntries.filter(entry => {
                    // Keep the entry we just edited and any non-absent entries
                    if (entry.id === entryId || entry.date !== targetDate || entry.status !== 'absent') {
                        return true;
                    }
                    // Filter out other absent entries for the same date
                    return false;
                });
            }
            
            await setDoc(attendanceRef, {
                staffId: staffId,
                staffName: staffMembers.find(s => s.id === staffId)?.name || 'Unknown',
                year: attendanceYear,
                month: attendanceMonth + 1,
                dailyEntries: updatedEntries,
                lastUpdated: new Date()
            }, { merge: true });
            
            // Update local state
            setAllStaffAttendance(prev => ({
                ...prev,
                [staffId]: updatedEntries
            }));
            
            setEditingEntry(null);
            toast.success('Attendance entry updated successfully');
        } catch (error) {
            console.error('Error updating attendance:', error);
            toast.error('Failed to update attendance entry');
        }
    };





    // New Attendance Entry Form Component
    const NewAttendanceEntryForm = ({ 
        staffId, 
        existingEntry, 
        onSave, 
        onCancel 
    }: { 
        staffId: string;
        existingEntry?: any;
        onSave: (data: any) => void;
        onCancel: () => void;
    }) => {
        const [formData, setFormData] = useState({
            date: existingEntry?.date || new Date().toISOString().split('T')[0],
            subscriptions: existingEntry?.subscriptions || [],
            totalHours: existingEntry?.totalHours || 0
        });
        
        // Track if current entry is marked as absent
        const currentEntryStatus = existingEntry?.status || null;

        const selectedStaff = staffMembers.find(s => s.id === staffId);
        const availableSubscriptions = getSubscriptionsForAttendanceEntry(staffId);

        // Handle marking as absent - clears form and submits
        const handleMarkAsAbsent = () => {
            // Check if month is locked
            if (isCurrentMonthLocked()) {
                toast.error('Cannot mark as absent - month is locked');
                return;
            }
            
            // Show immediate feedback
            toast.info('Clearing attendance and marking as absent...');
            
            // Clear the form first
            setFormData(prev => ({
                ...prev,
                subscriptions: [],
                totalHours: 0
            }));
            
            // Submit as absent immediately
            onSave({
                staffId,
                date: formData.date,
                subscriptions: [],
                totalHours: 0,
                status: 'absent'
            });
        };

        const handleSubscriptionHoursChange = (subscriptionId: string, hours: number) => {
            const updatedSubscriptions = formData.subscriptions.filter((sub: any) => sub.subscriptionId !== subscriptionId);
            
            if (hours > 0) {
                const subscription = availableSubscriptions.find((s: any) => s.id === subscriptionId);
                updatedSubscriptions.push({ 
                    subscriptionId, 
                    hours, 
                    subscriptionName: subscription?.Name || 'Unknown Subscription' 
                });
            }
            
            const totalHours = updatedSubscriptions.reduce((sum: number, sub: any) => sum + sub.hours, 0);
            
            setFormData({
                ...formData,
                subscriptions: updatedSubscriptions,
                totalHours
            });
        };

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            
            if (formData.totalHours > 0 && formData.subscriptions.length > 0) {
                onSave({
                    staffId,
                    date: formData.date,
                    subscriptions: formData.subscriptions,
                    totalHours: formData.totalHours
                });
            } else if (currentEntryStatus === 'absent') {
                // Allow submitting with 0 hours when updating an absent entry
                onSave({
                    staffId,
                    date: formData.date,
                    subscriptions: formData.subscriptions,
                    totalHours: formData.totalHours
                });
            } else {
                toast.error('Please add hours for at least one subscription');
            }
        };

        return (
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h4 className="text-lg font-semibold mb-4">
                    {existingEntry ? 'Edit Attendance Entry' : 'Add Attendance Entry'}
                    {selectedStaff && (
                        <span className="text-sm font-normal text-gray-600 ml-2">
                            for {selectedStaff.name}
                        </span>
                    )}
                </h4>
                
                {/* Month Lock Warning */}
                {isCurrentMonthLocked() && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-red-800">
                            <strong>Month Locked:</strong> Attendance cannot be modified for this month.
                        </p>
                    </div>
                )}

                {/* Current Entry Status */}
                {currentEntryStatus === 'absent' && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-orange-800">
                            <strong>Currently Marked as Absent:</strong> This date is marked as absent. You can update it by changing hours or marking absent again.
                        </p>
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Date Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                        <input
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>

                    {/* Subscription Hours */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hours by Subscription</label>
                        {availableSubscriptions.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">
                                <div className="text-2xl mb-2">🔒</div>
                                <p>No subscriptions available for attendance logging</p>
                            </div>
                        ) : (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {availableSubscriptions.map((subscription: any) => {
                                    const existingHours = formData.subscriptions.find((sub: any) => sub.subscriptionId === subscription.id)?.hours || 0;
                                    
                                return (
                                        <div key={subscription.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                        <div className="flex-1">
                                                <div className="font-medium text-sm">{subscription.Name}</div>
                                                <div className="text-xs text-gray-500">{subscription.Description}</div>
                                        </div>
                                            <div className="flex items-center space-x-2">
                                                <input
                                            type="number"
                                                    min="0"
                                                    max="24"
                                                    step="0.5"
                                                    value={existingHours}
                                            onChange={(e) => handleSubscriptionHoursChange(subscription.id, parseFloat(e.target.value) || 0)}
                                                    className="w-20 p-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                                    placeholder="0"
                                        />
                                                <span className="text-sm text-gray-600">hours</span>
                                            </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>

                    {/* Total Hours Display */}
                    <div className="bg-blue-50 p-3 rounded">
                        <div className="flex justify-between items-center">
                            <span className="font-medium text-blue-900">Total Hours:</span>
                            <span className="text-xl font-bold text-blue-600">{formData.totalHours} hours</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2 pt-4">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="outlined"
                            color="warning"
                            onClick={handleMarkAsAbsent}
                            disabled={isCurrentMonthLocked()}
                        >
                            Mark as Absent
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={(formData.totalHours === 0 && currentEntryStatus !== 'absent') || availableSubscriptions.length === 0 || isCurrentMonthLocked()}
                        >
                            {existingEntry ? 'Update Entry' : 'Add Entry'}
                        </Button>
                    </div>
                </form>
            </div>
        );
    };

    // Remove staff sync logic and staff Roles subcollection writes
    // Remove didAutoSync, migrationLoading, migrationProgress, handleMigrateToStaff, and related useEffect
    // Remove all code related to roleHistory, staff cards, and sync button
    // Only keep PendingActions and delegatedRoles logic for role management

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-6">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Team Management</h1>
                            <p className="mt-1 text-xs sm:text-sm text-gray-500">Manage roles and permissions</p>
                        </div>
                        {isOwnerOrAdmin && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon sx={{ fontSize: { xs: '16px', sm: '20px' } }} />}
                                onClick={handleAddMember}
                                className="bg-cc_brand hover:bg-cc_brand/90"
                                sx={{
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    px: { xs: 1.5, sm: 2 },
                                    py: { xs: 0.5, sm: 0.75 },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Add Member
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <Box sx={{ 
                    borderBottom: 1, 
                    borderColor: 'divider', 
                    mb: 2,
                    overflowX: 'auto',
                    '& .MuiTabs-scroller': {
                        overflowX: 'auto !important'
                    }
                }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        aria-label="team management tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: '80px', sm: 'auto' },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                    >
                        <Tab label="Team Overview" />
                        <Tab label="Staff" />
                        <Tab label="Attendance" />
                        <Tab label="Manage your academies" />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <TabPanel value={tabValue} index={0}>
                    <div className="space-y-6">
                        {/* My Roles Section */}
                    <div className="space-y-2 sm:space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <PersonIcon className="text-blue-600" sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-800">My Roles</h2>
                                    <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 rounded-full">{myRoles.length + 1}</span>
                        </div>
                                <IconButton 
                                    onClick={() => setMyRolesExpanded(!myRolesExpanded)}
                                    size="small"
                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                >
                                    {myRolesExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                                </IconButton>
                        </div>
                            {myRolesExpanded && (
                        <div className="space-y-2">
                            {/* Owner Account Item */}
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={handleClearRole}
                                onKeyDown={(e) => e.key === 'Enter' && handleClearRole()}
                                aria-label="Switch to personal account"
                                className={`
                                    p-2 sm:p-3 rounded-lg cursor-pointer transition-all
                                    ${!currentActiveRole
                                        ? 'bg-blue-50 border-l-4 border-blue-500'
                                        : 'bg-white border border-gray-200 hover:border-gray-300'
                                    }
                                `}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                                            {userData?.photo_url ? (
                                                <img src={userData.photo_url} alt={userData?.display_name || ''} className="h-full w-full object-cover" />
                                            ) : (
                                                <span className="text-xs sm:text-sm font-medium">
                                                    {(userData?.display_name || 'U').charAt(0).toUpperCase()}
                                                </span>
                                            )}
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                                {userData?.businessProfile?.businessName || userData?.display_name || 'My Account'}
                                            </p>
                                            <p className="text-[10px] sm:text-xs text-gray-500">Owner</p>
                                        </div>
                                    </div>
                                    {!currentActiveRole && (
                                        <CheckCircleIcon className="text-blue-500 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                    )}
                                </div>
                            </div>

                            {/* Delegated Roles */}
                            {myRoles && myRoles.length > 0 && myRoles.map((role: ServiceProviderRole) => (
                                        <div
                                            key={`${role.name}-${role.number}`}
                                            onClick={() => handleRoleSwitch(role)}
                                className={`
                                    p-2 sm:p-3 rounded-lg cursor-pointer transition-all
                                    ${role.number === currentActiveRole?.number
                                        ? 'bg-blue-50 border-l-4 border-blue-500'
                                        : 'bg-white border border-gray-200 hover:border-gray-300'
                                    }
                                `}
                                        >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                    <Avatar className="h-8 w-8 sm:h-9 sm:w-9">
                                                        {role.userPhoto ? (
                                                    <img src={role.userPhoto} alt={role.name} className="h-full w-full object-cover" />
                                                        ) : (
                                                            <span className="text-xs sm:text-sm font-medium">
                                                                {role.businessName.charAt(0).toUpperCase()}
                                                            </span>
                                                        )}
                                                    </Avatar>
                                            <div className="flex-1 min-w-0">
                                                        <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                                            {role.businessName}
                                                        </p>
                                                <p className="text-[10px] sm:text-xs text-gray-500">{role.role}</p>
                                                    </div>
                                                </div>
                                        {role.number === currentActiveRole?.number && (
                                            <CheckCircleIcon className="text-blue-500 w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                        </div>
                            )}
                    </div>

                        {/* Delegated Roles Section */}
                    <div className="space-y-2 sm:space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <GroupIcon className="text-green-600" sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-800">Team Members</h2>
                                    <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 rounded-full">{acceptedRoles.length}</span>
                                </div>
                                <div className="flex items-center gap-1 sm:gap-2">
                                    {isOwnerOrAdmin && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<AddIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />}
                                            onClick={handleAddDelegatedRole}
                                            sx={{
                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                px: { xs: 1, sm: 1.5 },
                                                py: { xs: 0.5, sm: 0.75 }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    )}
                                <IconButton 
                                    onClick={() => setDelegatedRolesExpanded(!delegatedRolesExpanded)}
                                    size="small"
                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                >
                                    {delegatedRolesExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                                </IconButton>
                                </div>
                                    </div>
                            {delegatedRolesExpanded && (
                                <>
                                    {/* Search Bar for Delegated Roles */}
                                    {acceptedRoles.length > 3 && (
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder="Search members..."
                                            value={delegatedRolesSearch}
                                            onChange={(e) => setDelegatedRolesSearch(e.target.value)}
                                            InputProps={{
                                                startAdornment: <SearchIcon className="text-gray-400 mr-2" sx={{ fontSize: { xs: '16px', sm: '18px' } }} />,
                                                sx: { fontSize: { xs: '14px', sm: '14px' } }
                                            }}
                                            InputLabelProps={{
                                                sx: { fontSize: { xs: '14px', sm: '14px' } }
                                            }}
                                        />
                                    )}
                                    
                                    {filteredDelegatedRoles.length > 0 ? (
                                        <div className="space-y-2">
                                            {filteredDelegatedRoles.map((role, index) => (
                                                <div key={index} className="bg-white p-2 sm:p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                            <Avatar src={role?.userPhoto} className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0">
                                                                <span className="text-xs sm:text-sm">{(role?.name || 'U').charAt(0)}</span>
                                                            </Avatar>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                                                                    <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{role?.name}</p>
                                                                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 whitespace-nowrap">{role?.role}</span>
                                                                </div>
                                                                <p className="text-[10px] sm:text-xs text-gray-500 truncate">{role?.number} • {role?.assignedSubscriptions?.length || 0} subscriptions</p>
                                                            </div>
                                                        </div>
                                                        {isOwnerOrAdmin && (isOwner || role.number !== userData?.phone_number) && (
                                                            <div className="flex gap-0.5 sm:gap-1 ml-1 sm:ml-2 flex-shrink-0">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleEditDelegatedRole(role)}
                                                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                                >
                                                                    <EditIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteDelegatedRole(role)}
                                                                    color="error"
                                                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                                >
                                                                    <DeleteIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                                                </IconButton>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : acceptedRoles.length > 0 ? (
                                        <div className="text-center py-6">
                                            <p className="text-sm text-gray-500">No members match your search.</p>
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            <GroupIcon className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                                            <p className="text-sm text-gray-500">No team members yet.</p>
                                    </div>
                                )}
                            </>
                            )}
                        </div>

                        {/* Pending Invites Section */}
                        {pendingInvites.length > 0 && (
                        <div className="space-y-2 sm:space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                    <GroupIcon className="text-orange-500" sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-800">Pending Invites</h2>
                                    <span className="text-[10px] sm:text-xs bg-orange-50 text-orange-600 px-1.5 sm:px-2 py-0.5 rounded-full">{pendingInvites.length}</span>
                                </div>
                                <IconButton 
                                    onClick={() => setPendingInvitesExpanded(!pendingInvitesExpanded)}
                                    size="small"
                                    sx={{ padding: { xs: '4px', sm: '8px' } }}
                                >
                                    {pendingInvitesExpanded ? <ExpandLessIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} /> : <ExpandMoreIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />}
                                </IconButton>
                            </div>
                            {pendingInvitesExpanded && (
                                <div className="space-y-2">
                                    {pendingInvites.map((member, index) => (
                                        <div key={index} className="bg-orange-50/50 p-2 sm:p-3 rounded-lg border border-orange-200">
                                            <div className="flex items-center gap-2 sm:gap-3">
                                                <Avatar className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-100 text-orange-600 flex-shrink-0">
                                                    <span className="text-xs sm:text-sm">{(member?.name || 'U').charAt(0)}</span>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                                                        <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{member?.name}</p>
                                                        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 whitespace-nowrap">{member?.role}</span>
                                                    </div>
                                                    <p className="text-[10px] sm:text-xs text-gray-500 truncate">{member?.number}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                        )}
                        </div>
                        )}
                    </div>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <div className="space-y-3 sm:space-y-4">
                        {/* Header with Sync Button */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
                            <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">Staff Management</h3>
                                <p className="text-xs sm:text-sm text-gray-500">Manage staff members</p>
                            </div>
                            {isOwner && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={syncStaffFromDelegatedRoles}
                                    disabled={syncLoading}
                                    startIcon={syncLoading ? <CircularProgress size={14} sx={{ fontSize: { xs: '14px', sm: '16px' } }} /> : <BadgeIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />}
                                    sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        px: { xs: 1.5, sm: 2 },
                                        py: { xs: 0.5, sm: 0.75 },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                >
                                    {syncLoading ? 'Syncing...' : 'Sync'}
                                </Button>
                            )}
                        </div>

                        {/* Staff List */}
                        {staffMembers.length > 0 ? (
                            <div className="space-y-2">
                                {staffMembers.map((member) => (
                                    <div key={member.id} className="bg-white rounded-lg border border-gray-200">
                                        {editingStaffId === member.id ? (
                                            // Edit Mode
                                            <div className="p-2 sm:p-3">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-3">
                                                    <TextField
                                                        label="Name"
                                                        value={editingStaff?.name || ''}
                                                        onChange={(e) => updateEditingStaff('name', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        InputProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                        InputLabelProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Phone"
                                                        value={editingStaff?.number || ''}
                                                        onChange={(e) => updateEditingStaff('number', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        InputProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                        InputLabelProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Email"
                                                        value={editingStaff?.email || ''}
                                                        onChange={(e) => updateEditingStaff('email', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        type="email"
                                                        InputProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                        InputLabelProps={{
                                                            sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                        }}
                                                    />
                                                    <FormControl size="small" fullWidth>
                                                        <Select
                                                            value={editingStaff?.role || ''}
                                                            onChange={(e) => updateEditingStaff('role', e.target.value)}
                                                            sx={{ fontSize: { xs: '14px', sm: '14px' } }}
                                                        >
                                                            <MenuItem value="Manager" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Manager</MenuItem>
                                                            <MenuItem value="Administrator" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Administrator</MenuItem>
                                                            <MenuItem value="Observer" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Observer</MenuItem>
                                                            <MenuItem value="Coach" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Coach</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl size="small" fullWidth>
                                                        <Select
                                                            value={editingStaff?.status || ''}
                                                            onChange={(e) => updateEditingStaff('status', e.target.value)}
                                                            sx={{ fontSize: { xs: '14px', sm: '14px' } }}
                                                        >
                                                            <MenuItem value="Active" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Active</MenuItem>
                                                            <MenuItem value="Inactive" sx={{ fontSize: { xs: '14px', sm: '14px' } }}>Inactive</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                                <div className="flex flex-col sm:flex-row justify-end gap-2">
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleSaveStaff}
                                                        size="small"
                                                        color="success"
                                                        sx={{
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                            px: { xs: 2, sm: 3 },
                                                            py: { xs: 0.5, sm: 0.75 },
                                                            width: { xs: '100%', sm: 'auto' }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={handleCancelEdit}
                                                        size="small"
                                                        sx={{
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                            px: { xs: 2, sm: 3 },
                                                            py: { xs: 0.5, sm: 0.75 },
                                                            width: { xs: '100%', sm: 'auto' }
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // View Mode
                                            <div className="p-2 sm:p-3">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                        <Avatar className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" src={member.photo}>
                                                            <span className="text-xs sm:text-sm">{member.name.charAt(0).toUpperCase()}</span>
                                                        </Avatar>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                                                                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{member.name}</p>
                                                                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                                                    <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 whitespace-nowrap">{member.role}</span>
                                                                    {member.status === 'Active' ? (
                                                                        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-green-50 text-green-700 whitespace-nowrap">Active</span>
                                                                    ) : (
                                                                        <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">Inactive</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p className="text-[10px] sm:text-xs text-gray-500 truncate">{member.number} {member.email && `• ${member.email}`}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-0.5 sm:gap-1 ml-1 sm:ml-2 flex-shrink-0">
                                                        <IconButton
                                                            onClick={() => handleEditStaff(member)}
                                                            size="small"
                                                            title="Edit"
                                                            sx={{ padding: { xs: '4px', sm: '8px' } }}
                                                        >
                                                            <EditIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleAccessPayroll(member)}
                                                            size="small"
                                                            title="Payroll"
                                                            sx={{ padding: { xs: '4px', sm: '8px' }, fontSize: { xs: '14px', sm: '16px' } }}
                                                        >
                                                            💰
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => handleAccessAttendance(member)}
                                                            size="small"
                                                            title="Attendance"
                                                            sx={{ padding: { xs: '4px', sm: '8px' }, fontSize: { xs: '14px', sm: '16px' } }}
                                                        >
                                                            📊
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <BadgeIcon className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                                <p className="text-sm text-gray-500">
                                    {isOwner ? "Click 'Sync' to add staff members." : "No staff members yet."}
                        </p>
                            </div>
                        )}
                    </div>
                </TabPanel>

                {/* Attendance Tab */}
                <TabPanel value={tabValue} index={2}>
                    <div className="space-y-3 sm:space-y-4">
                        <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Staff Attendance</h3>
                                    <p className="text-xs sm:text-sm text-gray-500">Daily logs for each staff member</p>
                            </div>
                                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center w-full sm:w-auto">
                                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 }, width: { xs: '100%', sm: 'auto' } }}>
                                        <Select 
                                            value={attendanceYear} 
                                            onChange={(e) => setAttendanceYear(Number(e.target.value))}
                                            sx={{ fontSize: { xs: '14px', sm: '14px' } }}
                                        >
                                            
                                            <MenuItem value={2024} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>2024</MenuItem>
                                            <MenuItem value={2025} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>2025</MenuItem>
                                            <MenuItem value={2026} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>2026</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 110 }, width: { xs: '100%', sm: 'auto' } }}>
                                        <Select 
                                            value={attendanceMonth} 
                                            onChange={(e) => setAttendanceMonth(Number(e.target.value))}
                                            sx={{ fontSize: { xs: '14px', sm: '14px' } }}
                                        >
                                            <MenuItem value={0} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>January</MenuItem>
                                            <MenuItem value={1} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>February</MenuItem>
                                            <MenuItem value={2} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>March</MenuItem>
                                            <MenuItem value={3} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>April</MenuItem>
                                            <MenuItem value={4} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>May</MenuItem>
                                            <MenuItem value={5} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>June</MenuItem>
                                            <MenuItem value={6} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>July</MenuItem>
                                            <MenuItem value={7} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>August</MenuItem>
                                            <MenuItem value={8} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>September</MenuItem>
                                            <MenuItem value={9} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>October</MenuItem>
                                            <MenuItem value={10} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>November</MenuItem>
                                            <MenuItem value={11} sx={{ fontSize: { xs: '14px', sm: '14px' } }}>December</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Button 
                                        variant="outlined" 
                                        size="small" 
                                        onClick={loadAllStaffAttendance}
                                        disabled={attendanceLoading}
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 0.5, sm: 0.75 },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        {attendanceLoading ? 'Loading...' : 'Load'}
                                    </Button>
                                </div>
                            </div>

                            {/* Loading State */}
                            {attendanceLoading && (
                                <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm text-center">
                                    <div className="text-blue-500 text-2xl sm:text-4xl mb-2 sm:mb-3">⏳</div>
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1 sm:mb-2">Loading Attendance Data</h3>
                                    <p className="text-xs sm:text-sm text-gray-500">Please wait while we fetch attendance records...</p>
                                                    </div>
                                        )}

                            {/* Error State */}
                            {attendanceError && !attendanceLoading && (
                                <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm border-l-4 border-red-500">
                                    <div className="text-red-500 text-2xl sm:text-4xl mb-2 sm:mb-3">⚠️</div>
                                    <h3 className="text-base sm:text-lg font-semibold text-red-700 mb-1 sm:mb-2">Error Loading Data</h3>
                                    <p className="text-xs sm:text-sm text-gray-600">{attendanceError}</p>
                                                            <Button 
                                        variant="outlined" 
                                        color="error" 
                                        onClick={loadAllStaffAttendance}
                                        className="mt-3 sm:mt-4"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            px: { xs: 2, sm: 3 },
                                            py: { xs: 0.5, sm: 0.75 },
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        Try Again
                                                            </Button>
                                            </div>
                                        )}

                                                    
                            {/* Staff Selection with Choice Chips */}
                            {!attendanceLoading && !attendanceError && (
                                                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                                    <div className="p-3 sm:p-6 border-b bg-gradient-to-r from-slate-50 to-blue-50">
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
                                            <div>
                                                <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">Select Staff Member</h4>
                                                <p className="text-xs sm:text-sm text-gray-600">View attendance calendar for {new Date(attendanceYear, attendanceMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                                                        </div>
                                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                                                {/* Month Lock Status */}
                                                <div className="flex items-center gap-2">
                                                    {isCurrentMonthLocked() ? (
                                                        <Chip 
                                                            icon={<span className="text-red-600 text-xs sm:text-sm">🔒</span>}
                                                            label="Month Locked" 
                                                            color="error" 
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                height: { xs: '24px', sm: '28px' }
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip 
                                                            icon={<span className="text-green-600 text-xs sm:text-sm">🔓</span>}
                                                            label="Month Unlocked" 
                                                            color="success" 
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                height: { xs: '24px', sm: '28px' }
                                                            }}
                                                        />
                                                    )}
                                                    
                                                    {/* Admin Lock/Unlock Button */}
                                                    {(isOwner || acceptedRoles.find(role => role.number === userData?.phone_number && role.role === 'Administrator')) && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={toggleMonthLock}
                                                            sx={{
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                px: { xs: 1.5, sm: 2 },
                                                                py: { xs: 0.5, sm: 0.75 },
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            disabled={loadingLockStatus}
                                                            startIcon={isCurrentMonthLocked() ? <span>🔓</span> : <span>🔒</span>}
                                                        >
                                                            {isCurrentMonthLocked() ? 'Unlock' : 'Lock'}
                                                        </Button>
                                                    )}
                                                    </div>
                                                
                                                <div className="text-xs sm:text-sm text-gray-500 bg-white px-2 sm:px-3 py-1 rounded-full border whitespace-nowrap">
                                                    {filteredStaffMembers.filter(staff => canManageAttendance(staff) || isOwner).length} staff
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Search Bar */}
                                        <div className="relative mb-3 sm:mb-4">
                                            <SearchIcon className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400" sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Search staff by name, email, role, or phone..."
                                                value={staffSearchQuery}
                                                onChange={(e) => setStaffSearchQuery(e.target.value)}
                                                InputProps={{
                                                    sx: { 
                                                        paddingLeft: { xs: '32px', sm: '40px' },
                                                        backgroundColor: 'white',
                                                        fontSize: { xs: '14px', sm: '14px' },
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#e5e7eb',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#d1d5db',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#3b82f6',
                                                            },
                                                        }
                                                    }
                                                }}
                                                InputLabelProps={{
                                                    sx: { fontSize: { xs: '14px', sm: '14px' } }
                                                }}
                                            />
                                            </div>
                                    
                                        {staffMembers.length === 0 ? (
                                            <div className="text-center py-6 sm:py-8">
                                                <div className="text-gray-400 text-2xl sm:text-4xl mb-2 sm:mb-3">👥</div>
                                                <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1 sm:mb-2">No Staff Members</h3>
                                                <p className="text-xs sm:text-sm text-gray-500">No staff members found to display attendance.</p>
                                                        </div>
                                        ) : filteredStaffMembers.filter(staff => canManageAttendance(staff) || isOwner).length === 0 ? (
                                            <div className="text-center py-6 sm:py-8">
                                                <div className="text-gray-400 text-2xl sm:text-4xl mb-2 sm:mb-3">🔍</div>
                                                <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1 sm:mb-2">No Matching Staff</h3>
                                                <p className="text-xs sm:text-sm text-gray-500">No staff members match your search criteria.</p>
                                                        </div>
                                        ) : (
                                            <div className="max-h-48 overflow-y-auto">
                                                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                    {filteredStaffMembers
                                                        .filter(staff => canManageAttendance(staff) || isOwner)
                                                        .map((staff) => {
                                                        const staffAttendance = allStaffAttendance[staff.id] || [];
                                                        const totalHours = staffAttendance.reduce((sum, entry) => sum + (entry.totalHours || 0), 0);
                                                        const presentDays = staffAttendance.filter(entry => entry.totalHours > 0).length;
                                                        const isSelected = selectedStaffForViewing?.id === staff.id;
                                                        
                                                        return (
                                                            <div
                                                                key={staff.id}
                                                                onClick={() => setSelectedStaffForViewing(staff)}
                                                                className={`
                                                                    cursor-pointer transition-all duration-200 rounded-lg p-2 sm:p-3 border-2
                                                                    ${isSelected 
                                                                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                                                                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                                                                    }
                                                                `}
                                                            >
                                                                <div className="flex items-center space-x-2 sm:space-x-3">
                                                                    <Avatar 
                                                                        src={staff.photo} 
                                                                        sx={{ 
                                                                            width: { xs: 28, sm: 36 }, 
                                                                            height: { xs: 28, sm: 36 },
                                                                            backgroundColor: isSelected ? '#3b82f6' : '#6b7280',
                                                                            fontSize: { xs: '12px', sm: '14px' }
                                                                        }}
                                                                    >
                                                                        {staff.name?.charAt(0)?.toUpperCase()}
                                                                    </Avatar>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className={`font-medium text-xs sm:text-sm truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                                                            {staff.name}
                                                        </div>
                                                                        <div className={`text-[10px] sm:text-xs truncate ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                                                                            {staff.role}
                                                    </div>
                                                        </div>
                                                                    <div className="text-right flex-shrink-0">
                                                                        <div className={`text-[10px] sm:text-xs font-semibold ${isSelected ? 'text-blue-800' : 'text-gray-700'}`}>
                                                                            {totalHours}h
                                                    </div>
                                                                        <div className={`text-[10px] sm:text-xs ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                                                                            {presentDays} days
                                                </div>
                                            </div>
                                    </div>
                                </div>
                                                        );
                                                    })}
                        </div>
                    </div>
                            )}
                        </div>

                                    {/* Selected Staff Attendance View */}
                                    {selectedStaffForViewing && (
                                        <div className="p-3 sm:p-6">
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
                                                <div className="flex items-center space-x-2 sm:space-x-4">
                                                    <Avatar 
                                                        src={selectedStaffForViewing.photo} 
                                                        sx={{ width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 }, fontSize: { xs: '16px', sm: '20px' } }}
                                                    >
                                                        {selectedStaffForViewing.name?.charAt(0)?.toUpperCase()}
                                                    </Avatar>
                                                    <div>
                                                        <h3 className="text-base sm:text-xl font-semibold text-gray-900">{selectedStaffForViewing.name}</h3>
                                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                                                            <span className="inline-flex px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                {selectedStaffForViewing.role}
                                                            </span>
                                                            <span className="truncate">{selectedStaffForViewing.email}</span>
                            </div>
                                                    </div>
                                                                    </div>
                                                <div className="text-left sm:text-right w-full sm:w-auto">
                                                    <div className="text-xs sm:text-sm text-gray-600">Total for Month</div>
                                                    <div className="text-lg sm:text-2xl font-bold text-green-600">
                                                        {(allStaffAttendance[selectedStaffForViewing.id] || []).reduce((sum, entry) => sum + (entry.totalHours || 0), 0)} hours
                                                            </div>
                                                    <div className="text-xs sm:text-sm text-gray-500">
                                                        {(allStaffAttendance[selectedStaffForViewing.id] || []).length} sessions
                                                            </div>
                                                                </div>
                            </div>

                                            {/* Insights Section */}
                                            {(() => {
                                                const staffAttendance = allStaffAttendance[selectedStaffForViewing.id] || [];
                                                
                                                if (staffAttendance.length === 0) return null;
                                                
                                                // Find busiest and freest days
                                                const busiestDay = staffAttendance.reduce((max, entry) => 
                                                    (entry.totalHours || 0) > (max.totalHours || 0) ? entry : max
                                                );
                                                
                                                const presentDays = staffAttendance.filter(entry => entry.totalHours > 0);
                                                const freestDay = presentDays.reduce((min, entry) => 
                                                    (entry.totalHours || Infinity) < (min.totalHours || Infinity) ? entry : min
                                                );
                                                
                                                // Calculate day of week patterns
                                                const dayOfWeekHours = Array(7).fill(0).map(() => ({ total: 0, count: 0 }));
                                                staffAttendance.forEach(entry => {
                                                    if (entry.totalHours > 0) {
                                                        const dayOfWeek = new Date(entry.date).getDay();
                                                        dayOfWeekHours[dayOfWeek].total += entry.totalHours;
                                                        dayOfWeekHours[dayOfWeek].count += 1;
                                                    }
                                                });
                                                
                                                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                const busiestDayOfWeek = dayOfWeekHours
                                                    .map((day, index) => ({ 
                                                        name: dayNames[index], 
                                                        avgHours: day.count > 0 ? (day.total / day.count) : 0,
                                                        count: day.count
                                                    }))
                                                    .filter(day => day.count > 0)
                                                    .sort((a, b) => (b.avgHours as number) - (a.avgHours as number))[0];

                                                return (
                                                    <div className="mb-6 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border border-blue-200 p-6">
                                                        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                                            <span className="text-2xl mr-2">📊</span>
                                                            Monthly Insights for {selectedStaffForViewing.name}
                                                        </h4>
                                                        
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            {/* Busiest Day */}
                                                            <div className="bg-white rounded-lg p-4 border border-emerald-200 shadow-sm">
                                                                <div className="flex items-center mb-2">
                                                                    <span className="text-lg mr-2">🔥</span>
                                                                    <h5 className="font-semibold text-emerald-700">Busiest Day</h5>
                                                                    </div>
                                                                <div className="text-2xl font-bold text-emerald-600 mb-1">
                                                                    {busiestDay.totalHours}h
                                                            </div>
                                                                <div className="text-sm text-gray-600">
                                                                    {new Date(busiestDay.date).toLocaleDateString('en-US', { 
                                                                        weekday: 'long', 
                                                                        month: 'short', 
                                                                        day: 'numeric' 
                                                                    })}
                                                            </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {busiestDay.subscriptions?.length || 0} subscription(s)
                                                                </div>
                                                    </div>

                                                            {/* Freest Day */}
                                                            <div className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                                                                <div className="flex items-center mb-2">
                                                                    <span className="text-lg mr-2">🕊️</span>
                                                                    <h5 className="font-semibold text-blue-700">Lightest Day</h5>
                                                        </div>
                                                                <div className="text-2xl font-bold text-blue-600 mb-1">
                                                                    {freestDay.totalHours}h
                                                                </div>
                                                                <div className="text-sm text-gray-600">
                                                                    {new Date(freestDay.date).toLocaleDateString('en-US', { 
                                                                        weekday: 'long', 
                                                                        month: 'short', 
                                                                        day: 'numeric' 
                                                                    })}
                                                                </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {freestDay.subscriptions?.length || 0} subscription(s)
                                                                </div>
                                                            </div>

                                                            {/* Busiest Day of Week */}
                                                            <div className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                                                                <div className="flex items-center mb-2">
                                                                    <span className="text-lg mr-2">📅</span>
                                                                    <h5 className="font-semibold text-purple-700">Busiest Weekday</h5>
                                                        </div>
                                                                <div className="text-2xl font-bold text-purple-600 mb-1">
                                                                    {busiestDayOfWeek?.name || 'N/A'}
                                                                                    </div>
                                                                                    <div className="text-sm text-gray-600">
                                                                    Avg: {typeof busiestDayOfWeek?.avgHours === 'number' ? busiestDayOfWeek.avgHours.toFixed(1) : '0'}h
                                                                                    </div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {busiestDayOfWeek?.count || 0} working {(busiestDayOfWeek?.name || '').toLowerCase()}s
                                                                                </div>
                                                                                </div>
                                                                            </div>
                                                    </div>
                                                );
                                            })()}

                                            {/* Monthly Calendar View */}
                                            {(() => {
                                                const staffAttendance = allStaffAttendance[selectedStaffForViewing.id] || [];
                                                
                                                // Create a map of dates to attendance entries for quick lookup (excluding deleted entries)
                                                const attendanceByDate = staffAttendance
                                                    .filter(entry => entry.status !== 'deleted')
                                                    .reduce((acc: {[key: string]: any}, entry) => {
                                                        acc[entry.date] = entry;
                                                        return acc;
                                                    }, {});

                                                // Get the current month's calendar data
                                                const year = attendanceYear;
                                                const month = attendanceMonth;
                                                const daysInMonth = new Date(year, month + 1, 0).getDate();
                                                const firstDayOfMonth = new Date(year, month, 1).getDay();
                                                
                                                // Create calendar grid
                                                const calendarDays = [];
                                                
                                                // Add empty cells for days before the first day of the month
                                                for (let i = 0; i < firstDayOfMonth; i++) {
                                                    calendarDays.push(null);
                                                }
                                                
                                                // Add all days of the month
                                                for (let day = 1; day <= daysInMonth; day++) {
                                                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                                    const entry = attendanceByDate[dateStr];
                                                    calendarDays.push({
                                                        day,
                                                        date: dateStr,
                                                        entry,
                                                        totalHours: entry?.totalHours || 0,
                                                        subscriptions: entry?.subscriptions || []
                                                    });
                                                }

                                                return (
                                                    <div className="space-y-4">
                                                                            <div className="flex items-center justify-between">
                                                            <h4 className="text-lg font-semibold text-gray-800">Monthly Attendance Calendar</h4>
                                                            <div className="flex items-center space-x-4 text-xs text-gray-600">
                                                                <div className="flex items-center space-x-1">
                                                                    <div className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded"></div>
                                                                    <span>Present</span>
                                                                </div>
                                                                <div className="flex items-center space-x-1">
                                                                    <div className="w-3 h-3 bg-slate-100 border border-slate-300 rounded"></div>
                                                                    <span>Absent</span>
                                                                </div>
                                                            </div>
                                                    </div>

                                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
                                                            {/* Calendar Header */}
                                                            <div className="grid grid-cols-7 bg-gradient-to-r from-slate-100 via-gray-100 to-slate-100 border-b border-gray-300">
                                                                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                                                                    <div key={day} className="p-4 text-center text-sm font-bold text-gray-700 border-r border-gray-300 last:border-r-0">
                                                                        <div className="hidden sm:block">{day}</div>
                                                                        <div className="sm:hidden">{day.substr(0, 3)}</div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Calendar Body */}
                                                            <div className="grid grid-cols-7">
                                                                {calendarDays.map((dayData, index) => {
                                                                    if (!dayData) {
                                                                        return (
                                                                            <div key={index} className="h-28 border-r border-b border-gray-200 bg-gradient-to-br from-gray-50 to-slate-100 last:border-r-0"></div>
                                                                        );
                                                                    }

                                                    const { day, entry, totalHours, subscriptions } = dayData;
                                                    const isPresent = entry && entry.status === 'present' && totalHours > 0;
                                                    const isExplicitAbsent = entry && entry.status === 'absent';
                                                    const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();

                                                    // Create tooltip content with audit log
                                                    let tooltipContent = '';
                                                    if (isPresent) {
                                                        tooltipContent = `${totalHours}h total\n${subscriptions.map((sub: any) => `${sub.subscriptionName}: ${sub.hours}h`).join('\n')}`;
                                                    } else if (isExplicitAbsent) {
                                                        tooltipContent = 'Explicitly marked as Absent';
                                                    } else {
                                                        tooltipContent = 'No attendance record';
                                                    }
                                                    
                                                    // Add audit log to tooltip (for admins only)
                                                    const currentUserRole = acceptedRoles.find(role => role.number === userData?.phone_number);
                                                    const isUserAdmin = isOwner || currentUserRole?.role === 'Administrator';
                                                    
                                                    if (entry && entry.auditLog && entry.auditLog.length > 0 && isUserAdmin) {
                                                        tooltipContent += '\n\n🔍 Admin Audit Log (Last 3 entries):';
                                                        entry.auditLog.slice(-3).forEach((log: any, index: number) => {
                                                            const timestamp = new Date(log.timestamp.toDate ? log.timestamp.toDate() : log.timestamp).toLocaleString();
                                                            tooltipContent += `\n${index + 1}. ${timestamp}`;
                                                            tooltipContent += `\n   Action: ${log.action} by ${log.user}`;
                                                            if (log.details) tooltipContent += `\n   Details: ${log.details}`;
                                                            if (index < entry.auditLog.slice(-3).length - 1) tooltipContent += '\n';
                                                        });
                                                    }

                                                                    return (
                                                                        <div
                                                                            key={day}
                                                                            className={`h-28 border-r border-b border-gray-300 last:border-r-0 relative transition-all duration-300 group ${
                                                                                isCurrentMonthLocked() 
                                                                                    ? 'opacity-75 cursor-not-allowed' 
                                                                                    : 'cursor-pointer hover:shadow-lg hover:scale-105 transform'
                                                                            } ${
                                                                                isToday ? 'ring-2 ring-blue-500 ring-inset shadow-md' : ''
                                                            } ${
                                                                isPresent 
                                                                    ? 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 border-emerald-300 shadow-sm'
                                                                    : isExplicitAbsent
                                                                        ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 hover:from-orange-100 hover:via-amber-100 hover:to-yellow-100 border-orange-300 shadow-sm'
                                                                        : 'bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 hover:from-slate-100 hover:via-gray-100 hover:to-zinc-100 border-slate-300'
                                                            } ${
                                                                isCurrentMonthLocked() ? 'bg-opacity-60' : ''
                                                            }`}
                                                                            title={tooltipContent}
                                                                            onClick={(e) => {
                                                                                if (isCurrentMonthLocked()) {
                                                                                    toast.error('Month is locked - no modifications allowed');
                                                                                    return;
                                                                                }
                                                                                
                                                                                if (e.shiftKey && !entry && canManageAttendance(selectedStaffForViewing)) {
                                                                                    // Shift+click to mark absent
                                                                                    handleAddAbsentEntry(selectedStaffForViewing.id, dayData.date);
                                                                                } else if (entry && canManageAttendance(selectedStaffForViewing)) {
                                                                                    // Edit existing entry
                                                                                    setEditingEntry(entry);
                                                                                    setIsAddingNewEntry(false);
                                                                                } else if (!entry && canManageAttendance(selectedStaffForViewing)) {
                                                                                    // Add new entry (present)
                                                                                    setIsAddingNewEntry(true);
                                                                                    setEditingEntry({
                                                                                        date: dayData.date,
                                                                                        subscriptions: [],
                                                                                        totalHours: 0
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            {/* Day number */}
                                                                            <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                                                isToday 
                                                                                    ? 'bg-blue-500 text-white shadow-md' 
                                                                                    : isPresent 
                                                                                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                                                                        : isExplicitAbsent
                                                                                            ? 'bg-orange-100 text-orange-700 border border-orange-300'
                                                                                            : 'bg-gray-100 text-gray-600 border border-gray-300'
                                                                            }`}>
                                                                                {day}
                                                        </div>

                                                                            {/* Edit Icon - Always Visible */}
                                                                            {canManageAttendance(selectedStaffForViewing) && (
                                                                                <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-sm hover:bg-blue-600 transition-colors duration-200">
                                                                                    <EditIcon sx={{ fontSize: 12, color: 'white' }} />
                                                            </div>
                                                        )}
                                                        
                                                                            {/* Hours display */}
                                                                            <div className="absolute bottom-2 left-2 right-2">
                                                                                {isPresent ? (
                                                                                    <div className="text-center bg-white bg-opacity-90 rounded-lg py-1 px-2 backdrop-blur-sm">
                                                                                        <div className="text-sm font-bold text-emerald-700">
                                                                                            {totalHours}h
                                                            </div>
                                                                                        {subscriptions.length > 1 && (
                                                                                            <div className="text-xs text-emerald-600">
                                                                                                {subscriptions.length} subs
                                                        </div>
                                                                                        )}
                                                    </div>
                                                                                ) : isExplicitAbsent ? (
                                                                                    <div className="text-center bg-orange-100 bg-opacity-90 rounded-lg py-1 px-2 backdrop-blur-sm">
                                                                                        <div className="text-xs text-orange-700 font-medium">
                                                                                            Absent
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-center bg-slate-100 bg-opacity-90 rounded-lg py-1 px-2 backdrop-blur-sm">
                                                                                        <div className="text-xs text-slate-600 font-medium">
                                                                                            No Record
                                                                                        </div>
                                                                                        <div className="text-xs text-slate-500 mt-1">
                                                                                            Shift+Click for Absent
                                                </div>
                                            </div>
                                        )}
                                                                                </div>

                                                                    </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            </div>

                                                        {/* Monthly Summary */}
                                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gradient-to-r from-slate-50 to-blue-50 p-6 rounded-lg border border-slate-200">
                                                            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                                                <div className="text-2xl font-bold text-emerald-600">
                                                                    {staffAttendance.filter(entry => entry.totalHours > 0).length}
                                                        </div>
                                                                <div className="text-sm text-gray-600 font-medium">Present Days</div>
                                                    </div>
                                                            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                                                <div className="text-2xl font-bold text-slate-600">
                                                                    {daysInMonth - staffAttendance.filter(entry => entry.totalHours > 0).length}
                                                </div>
                                                                <div className="text-sm text-gray-600 font-medium">Absent Days</div>
                                            </div>
                                                            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                                                <div className="text-2xl font-bold text-blue-600">
                                                                    {staffAttendance.reduce((sum, entry) => sum + (entry.totalHours || 0), 0)}h
                                                        </div>
                                                                <div className="text-sm text-gray-600 font-medium">Total Hours</div>
                                                        </div>
                                                            <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                                                <div className="text-2xl font-bold text-indigo-600">
                                                                    {staffAttendance.length > 0 
                                                                        ? (staffAttendance.reduce((sum, entry) => sum + (entry.totalHours || 0), 0) / staffAttendance.filter(entry => entry.totalHours > 0).length).toFixed(1)
                                                                        : '0'
                                                                    }h
                                                        </div>
                                                                <div className="text-sm text-gray-600 font-medium">Avg Hours/Day</div>
                                                        </div>
                                                    </div>

                                                        {/* Edit Form Modal */}
                                                        {editingEntry && (
                                                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                                                                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                                                                    <div className="p-6">
                                                                        <div className="flex items-center justify-between mb-4">
                                                                            <h3 className="text-lg font-semibold">
                                                                                {isAddingNewEntry ? 'Add' : 'Edit'} Attendance - {new Date(editingEntry.date).toLocaleDateString()}
                                                                            </h3>
                                                                            <div className="flex space-x-2">
                                                                                {!isAddingNewEntry && (
                                                                                    <Button
                                                                                        variant="outlined"
                                                                                        color="error"
                                                                                        size="small"
                                                                                        onClick={() => {
                                                                                            setEntryToDelete(editingEntry);
                                                                                            setShowDeleteConfirm(true);
                                                                                        }}
                                                                                >
                                                                                    Delete
                                                                                </Button>
                                                                                )}
                                                                                <IconButton onClick={() => {
                                                                                    setEditingEntry(null);
                                                                                    setIsAddingNewEntry(false);
                                                                                }}>
                                                                                    <DeleteIcon />
                                                                                </IconButton>
                                                        </div>
                                                    </div>
                                                                        <NewAttendanceEntryForm
                                                                            staffId={selectedStaffForViewing.id}
                                                                            existingEntry={editingEntry}
                                                                            onSave={(data) => {
                                                                                if (isAddingNewEntry) {
                                                                                    handleAddAttendanceEntry({
                                                                                        staffId: selectedStaffForViewing.id,
                                                                                        ...data
                                                                                    });
                                                                                } else {
                                                                                    handleEditAttendanceEntry(selectedStaffForViewing.id, editingEntry.id, data);
                                                                                }
                                                                                setEditingEntry(null);
                                                                                setIsAddingNewEntry(false);
                                                                            }}
                                                                            onCancel={() => {
                                                                                setEditingEntry(null);
                                                                                setIsAddingNewEntry(false);
                                                                            }}
                                                                        />
                                                        </div>
                                                    </div>
                                                </div>
                                                        )}
                                                        </div>
                                                );
                                            })()}
                                            </div>
                                        )}

                                    {/* Summary when no staff selected */}
                                    {!selectedStaffForViewing && staffMembers.length > 0 && (
                                        <div className="p-6 bg-gray-50 border-t">
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-600">Total Staff</div>
                                                    <div className="text-2xl font-bold text-blue-600">{staffMembers.length}</div>
                                                        </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-600">Total Hours</div>
                                                    <div className="text-2xl font-bold text-green-600">
                                                        {Object.values(allStaffAttendance).flat().reduce((sum, entry) => sum + (entry.totalHours || 0), 0)}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-600">Total Sessions</div>
                                                    <div className="text-2xl font-bold text-purple-600">
                                                        {Object.values(allStaffAttendance).flat().length}
                                            </div>
                                                    </div>
                                                <div>
                                                    <div className="text-sm font-medium text-gray-600">Active Staff</div>
                                                    <div className="text-2xl font-bold text-orange-600">
                                                        {staffMembers.filter(staff => (allStaffAttendance[staff.id] || []).length > 0).length}
                                                        </div>
                                                    </div>
                                    </div>
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                </TabPanel>

                {/* Super Admin Tab */}
                <TabPanel value={tabValue} index={3}>
                    <div className="space-y-4 sm:space-y-6">
                        {/* Header with Request Button */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Access & Permissions</h3>
                                <p className="text-xs sm:text-sm text-gray-500">
                                    Manage who can view your data and which academies you can access
                                </p>
                            </div>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setSuperAdminModalOpen(true);
                                    setSuperAdminPhoneNumber('');
                                    setSuperAdminSearchResult(null);
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                            >
                                Request Access
                            </Button>
                        </div>

                        {superAdminRequestsLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <CircularProgress size={32} />
                            </div>
                        ) : (
                            <>
                            {/* Top Row: Academies I Can Access & People with Access to My Data */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                                {/* Academies I Can Access */}
                                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                    <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                                        <div className="flex items-center gap-2">
                                            <span className="text-green-600">🏫</span>
                                            <h4 className="font-medium text-gray-800">Academies I Can Access</h4>
                                            {mySuperAdminRoles.length > 0 && (
                                                <Chip 
                                                    label={mySuperAdminRoles.length} 
                                                    size="small" 
                                                    color="success"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">View their payment & revenue data</p>
                                    </div>
                                    <div className="p-4 max-h-48 overflow-y-auto">
                                        {mySuperAdminRoles.length === 0 ? (
                                            <div className="text-center py-4">
                                                <div className="text-gray-300 text-2xl mb-1">🔒</div>
                                                <p className="text-sm text-gray-500">No academies connected yet</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {mySuperAdminRoles.map((role) => (
                                                    <div key={role.id} className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2 border border-green-300">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                {role.approverBusinessName || 'Unknown Academy'}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {role.approverPhoneNumber}
                                                            </p>
                                                        </div>
                                                        <Chip 
                                                            label="Active" 
                                                            size="small" 
                                                            color="success"
                                                            sx={{ height: 20, fontSize: '0.65rem' }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* People with Access to My Data */}
                                <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                    <div className="bg-purple-50 px-4 py-3 border-b border-purple-200">
                                        <div className="flex items-center gap-2">
                                            <span className="text-purple-600">👥</span>
                                            <h4 className="font-medium text-gray-800">People with Access to My Data</h4>
                                            {mySuperAdmins.length > 0 && (
                                                <Chip 
                                                    label={mySuperAdmins.length} 
                                                    size="small" 
                                                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#9333ea', color: 'white' }}
                                                />
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">Can view your payment & revenue information</p>
                                    </div>
                                    <div className="p-4 max-h-48 overflow-y-auto">
                                        {mySuperAdmins.length === 0 ? (
                                            <div className="text-center py-4">
                                                <div className="text-gray-300 text-2xl mb-1">👤</div>
                                                <p className="text-sm text-gray-500">No one has access yet</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {mySuperAdmins.map((admin) => (
                                                    <div key={admin.id} className="flex items-center justify-between bg-purple-50 rounded-lg px-3 py-2 border border-purple-300">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                {admin.name || 'Unknown'}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {admin.phoneNumber}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={actionLoadingId === admin.id}
                                                            onClick={async () => {
                                                                setActionLoadingId(admin.requestId);
                                                                try {
                                                                    await axiosPostRequest(
                                                                        `${SECRETS_ENV.API_END_POINT}/api/superAdmin/revoke`,
                                                                        { approverSuperAdminDocId : admin.id, approverSPID : serviceProviderId }
                                                                    );
                                                                    toast.success('Access revoked successfully');
                                                                    fetchSuperAdminRequests();
                                                                } catch (error) {
                                                                    toast.error('Failed to revoke access');
                                                                } finally {
                                                                    setActionLoadingId(null);
                                                                }
                                                            }}
                                                            sx={{ 
                                                                textTransform: 'none', 
                                                                fontSize: '0.65rem',
                                                                minWidth: 'auto',
                                                                px: 1.5,
                                                                py: 0.25
                                                            }}
                                                        >
                                                            {actionLoadingId === admin.requestId ? (
                                                                <CircularProgress size={12} color="error" />
                                                            ) : (
                                                                'Revoke'
                                                            )}
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Pending Requests - Merged Section */}
                            <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
                                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200">
                                    <div className="flex items-center gap-2">
                                        <span className="text-amber-600">⏳</span>
                                        <h4 className="font-medium text-gray-800">Pending Requests</h4>
                                        {(receivedRequests.filter(r => r.status === 'pending').length + sentRequests.filter(r => r.status === 'pending').length) > 0 && (
                                            <Chip 
                                                label={receivedRequests.filter(r => r.status === 'pending').length + sentRequests.filter(r => r.status === 'pending').length} 
                                                size="small" 
                                                color="warning"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">Requests awaiting action</p>
                                </div>
                                <div className="p-4">
                                    {(receivedRequests.filter(r => r.status === 'pending').length + sentRequests.filter(r => r.status === 'pending').length) === 0 ? (
                                        <div className="text-center py-6">
                                            <div className="text-gray-300 text-3xl mb-2">📭</div>
                                            <p className="text-sm text-gray-500">No pending requests</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Click "Request Access" to send a new request
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {/* Requests received - They sent a request */}
                                            {receivedRequests.filter(r => r.status === 'pending').map((request) => (
                                                <div key={request.id} className="bg-amber-50 rounded-lg p-3 border border-amber-300">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-amber-600">📥</span>
                                                        <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">Incoming Request</span>
                                                    </div>
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                {request.initiatorBusinessName || 'Unknown Academy'}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {request.initiatorPhoneNumber}
                                                            </p>
                                                            <p className="text-xs text-amber-700 mt-1 font-medium">
                                                                They have sent a request for super admin access to your account
                                                            </p>
                                                            {request.createdAt && (
                                                                <p className="text-[10px] text-gray-400 mt-1">
                                                                    Received: {new Date(request.createdAt?.seconds * 1000 || request.createdAt).toLocaleDateString()}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 mt-3">
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            disabled={actionLoadingId === request.id}
                                                            onClick={async () => {
                                                                setActionLoadingId(request.id);
                                                                try {
                                                                    await axiosPostRequest(
                                                                        `${SECRETS_ENV.API_END_POINT}/api/superAdmin/approve`,
                                                                        { requestID: request.id }
                                                                    );
                                                                    toast.success('Request approved!');
                                                                    fetchSuperAdminRequests();
                                                                } catch (error) {
                                                                    toast.error('Failed to approve request');
                                                                } finally {
                                                                    setActionLoadingId(null);
                                                                }
                                                            }}
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1 }}
                                                        >
                                                            {actionLoadingId === request.id ? <CircularProgress size={14} /> : 'Accept'}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            disabled={actionLoadingId === request.id}
                                                            onClick={async () => {
                                                                setActionLoadingId(request.id);
                                                                try {
                                                                    await axiosPostRequest(
                                                                        `${SECRETS_ENV.API_END_POINT}/api/superAdmin/reject`,
                                                                        { requestID: request.id }
                                                                    );
                                                                    toast.success('Request rejected');
                                                                    fetchSuperAdminRequests();
                                                                } catch (error) {
                                                                    toast.error('Failed to reject request');
                                                                } finally {
                                                                    setActionLoadingId(null);
                                                                }
                                                            }}
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1 }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Requests sent - You sent a request */}
                                            {sentRequests.filter(r => r.status === 'pending').map((request) => (
                                                <div key={request.id} className="bg-blue-50 rounded-lg p-3 border border-blue-300">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-blue-600">📤</span>
                                                        <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded">Outgoing Request</span>
                                                    </div>
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-medium text-gray-800 truncate">
                                                                {request.approverBusinessName || 'Unknown Academy'}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                {request.approverPhoneNumber}
                                                            </p>
                                                            <p className="text-xs text-blue-700 mt-1 font-medium">
                                                                You sent a request for super admin access
                                                            </p>
                                                            {request.createdAt && (
                                                                <p className="text-[10px] text-gray-400 mt-1">
                                                                    Sent: {new Date(request.createdAt?.seconds * 1000 || request.createdAt).toLocaleDateString()}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <Chip 
                                                            label="Pending" 
                                                            size="small"
                                                            color="warning"
                                                            sx={{ height: 22, fontSize: '0.65rem' }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            </>
                        )}

                        {/* Info note */}
                        <div className="bg-blue-50 border border-blue-300 rounded-lg p-3">
                            <p className="text-sm text-blue-800">
                                <strong>Note:</strong> When you get access to an academy, you can view their payment and revenue information from the Access Control dashboard.
                            </p>
                        </div>
                    </div>
                </TabPanel>

                {/* Super Admin Request Modal */}
                <Dialog 
                    open={superAdminModalOpen} 
                    onClose={() => {
                        setSuperAdminModalOpen(false);
                        setSuperAdminPhoneNumber('');
                        setSuperAdminSearchResult(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle className="text-lg font-semibold text-gray-800">
                        Request Access to an Academy
                    </DialogTitle>
                    <DialogContent>
                        <div className="py-2">
                            <p className="text-sm text-gray-600 mb-4">
                                Enter the academy owner's phone number to request access.
                            </p>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Mobile Number
                                </label>
                                <PhoneInput
                                    country={'in'}
                                    value={superAdminPhoneNumber}
                                    onChange={(value) => {
                                        setSuperAdminPhoneNumber(value);
                                        setSuperAdminSearchResult(null);
                                    }}
                                    inputStyle={{
                                        width: '100%',
                                        height: '40px',
                                        fontSize: '14px',
                                        borderRadius: '8px',
                                        borderColor: '#d1d5db'
                                    }}
                                    containerStyle={{
                                        width: '100%'
                                    }}
                                />
                            </div>

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={async () => {
                                    if (superAdminPhoneNumber.length < 10) {
                                        toast.error('Please enter a valid phone number');
                                        return;
                                    }
                                    
                                    const phoneWithPlus = '+' + superAdminPhoneNumber;
                                    
                                    // Check if user is trying to add their own number
                                    if (userData?.phone_number === phoneWithPlus) {
                                        toast.error('You cannot request super admin access to your own account');
                                        return;
                                    }
                                    
                                    setSuperAdminSearchLoading(true);
                                    setSuperAdminSearchResult(null);
                                    
                                    try {
                                        const serviceProvidersQuery = query(
                                            collection(db, 'ServiceProviders'),
                                            where('phone_number', '==', phoneWithPlus)
                                        );
                                        
                                        const querySnapshot = await getDocs(serviceProvidersQuery);
                                        
                                        if (!querySnapshot.empty) {
                                            const docData = querySnapshot.docs[0].data();
                                            setSuperAdminSearchResult({
                                                found: true,
                                                academyName: docData?.businessProfile?.businessName || docData.name || 'Unknown Academy',
                                                phoneNumber: phoneWithPlus,
                                                serviceProviderId: querySnapshot.docs[0].id
                                            });
                                        } else {
                                            setSuperAdminSearchResult({
                                                found: false
                                            });
                                        }
                                    } catch (error) {
                                        console.error('Error fetching service provider:', error);
                                        toast.error('Error searching for academy');
                                    } finally {
                                        setSuperAdminSearchLoading(false);
                                    }
                                }}
                                disabled={superAdminSearchLoading || superAdminPhoneNumber.length < 10}
                                sx={{ textTransform: 'none' }}
                            >
                                {superAdminSearchLoading ? (
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                ) : null}
                                {superAdminSearchLoading ? 'Searching...' : 'Fetch Details'}
                            </Button>

                            {/* Search Results */}
                            {superAdminSearchResult && (
                                <div className="mt-4">
                                    {superAdminSearchResult.found ? (
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CheckCircleIcon className="text-green-600" fontSize="small" />
                                                <span className="text-sm font-medium text-gray-800">Academy Found</span>
                                            </div>
                                            <div className="space-y-1 text-sm">
                                                <p className="text-gray-600">
                                                    <span className="font-medium">Name:</span> {superAdminSearchResult.academyName}
                                                </p>
                                                <p className="text-gray-600">
                                                    <span className="font-medium">Phone:</span> {superAdminSearchResult.phoneNumber}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                            <p className="text-sm text-amber-800">
                                                <strong>No account found.</strong> Please check the phone number and try again.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions className="px-6 pb-4">
                        <Button 
                            onClick={() => {
                                setSuperAdminModalOpen(false);
                                setSuperAdminPhoneNumber('');
                                setSuperAdminSearchResult(null);
                            }}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        {superAdminSearchResult?.found && (
                            <Button 
                                onClick={async () => {
                                    setSuperAdminRequestLoading(true);
                                    
                                    try {
                                        const requestBody = {
                                            initiatorSPID: serviceProviderId,
                                            approverSPID: superAdminSearchResult.serviceProviderId,
                                            initiatorBusinessName: userData?.businessProfile?.businessName || 'Unknown',
                                            approverBusinessName: superAdminSearchResult.academyName,
                                            initiatorPhoneNumber: userData?.phone_number,
                                            approverPhoneNumber: superAdminSearchResult.phoneNumber
                                        };
                                        
                                        const response = await axiosPostRequest(
                                            `${SECRETS_ENV.API_END_POINT}/api/superAdmin/request`,
                                            requestBody
                                        );
                                        
                                        if (response.message === "Request successful" || (response as any).success) {
                                            toast.success('Super Admin request sent successfully!');
                                            setSuperAdminModalOpen(false);
                                            setSuperAdminPhoneNumber('');
                                            setSuperAdminSearchResult(null);
                                        } else {
                                            toast.error(response.message || 'Failed to send request');
                                        }
                                    } catch (error: any) {
                                        console.error('Error sending super admin request:', error);
                                        toast.error(error?.response?.data?.message || 'Failed to send request');
                                    } finally {
                                        setSuperAdminRequestLoading(false);
                                    }
                                }}
                                variant="contained"
                                color="primary"
                                disabled={superAdminRequestLoading}
                            >
                                {superAdminRequestLoading ? (
                                    <>
                                        <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                        Sending...
                                    </>
                                ) : (
                                    'Send Request'
                                )}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                {/* Payroll Modal */}
                <Dialog 
                    open={payrollModalOpen} 
                    onClose={handleClosePayrollModal}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle className="bg-gradient-to-r from-purple-500 to-blue-500 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">💰</span>
                            <div>
                                <h2 className="text-xl font-semibold">Payroll Management</h2>
                                <p className="text-sm opacity-90">{selectedStaffForPayroll?.name} ({selectedStaffForPayroll?.role})</p>
                            </div>
                        </div>
                        <Button 
                            onClick={handleClosePayrollModal} 
                            variant="outlined" 
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Close
                        </Button>
                    </DialogTitle>
                    <DialogContent className="p-0 max-h-[70vh] overflow-hidden">
                        <div className="h-full flex flex-col">
                            <div className="p-6 flex-1 overflow-y-auto">
                                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg text-center">
                                                    <div className="text-gray-400 text-6xl mb-4">💰</div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Payroll Management</h3>
                                    <p className="text-gray-500">Payroll features will be available in future updates.</p>
                                                </div>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Attendance Modal */}
                <Dialog 
                    open={attendanceModalOpen} 
                    onClose={handleCloseAttendanceModal}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle className="bg-gradient-to-r from-green-500 to-teal-500 text-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">📊</span>
                            <div>
                                <h2 className="text-xl font-semibold">Attendance Management</h2>
                                <p className="text-sm opacity-90">{selectedStaffForAttendance?.name} ({selectedStaffForAttendance?.role})</p>
                            </div>
                        </div>
                        <Button 
                            onClick={handleCloseAttendanceModal} 
                            variant="outlined" 
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                        >
                            Close
                        </Button>
                    </DialogTitle>
                    <DialogContent className="p-0 max-h-[70vh] overflow-hidden">
                        <div className="h-full flex flex-col">
                            <div className="p-6 flex-1 overflow-y-auto">
                                <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg text-center">
                                                    <div className="text-gray-400 text-6xl mb-4">⏰</div>
                                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Individual Attendance Management</h3>
                                    <p className="text-gray-500">Use the main attendance tab to view and manage staff attendance records.</p>
                                                </div>
                                                    </div>
                                                </div>
                    </DialogContent>
                </Dialog>

                {/* Custom Delete Confirmation Modal */}
                <Dialog 
                    open={showDeleteConfirm} 
                    onClose={() => setShowDeleteConfirm(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle className="text-lg font-semibold text-gray-800">
                        Confirm Delete
                    </DialogTitle>
                    <DialogContent>
                        <div className="py-4">
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to delete this attendance entry? 
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                <p className="text-sm text-amber-800">
                                    <strong>Note:</strong> This will mark the entry as deleted but preserve it for audit purposes. 
                                    The entry will no longer appear in reports but can be recovered if needed.
                                </p>
                                                </div>
                            {entryToDelete && (
                                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <p className="text-sm text-gray-700">
                                        <strong>Entry Details:</strong>
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Date: {new Date(entryToDelete.date).toLocaleDateString()}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Hours: {entryToDelete.totalHours}h
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Subscriptions: {entryToDelete.subscriptions?.length || 0}
                                    </p>
                                    </div>
                                )}
                        </div>
                    </DialogContent>
                    <DialogActions className="px-6 pb-4">
                        <Button 
                            onClick={() => setShowDeleteConfirm(false)}
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={() => {
                                if (entryToDelete) {
                                    handleSoftDeleteAttendance(selectedStaffForViewing.id, entryToDelete.id);
                                }
                            }}
                            color="error"
                            variant="contained"
                        >
                            Delete Entry
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
} 
